// © 2026 Clarion. All rights reserved.
// Clarion — theclarion.app
// Unauthorized copying, modification, or distribution of this software is strictly prohibited.

import { useState, useEffect, useRef, Component } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPA_URL = "https://qizqfeibqfqvfdzvqtin.supabase.co";
const SUPA_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFpenFmZWlicWZxdmZkenZxdGluIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4MjQ3MzcsImV4cCI6MjA4ODQwMDczN30.oBiHaOZYbNi6-PyoAFeE_eUoaPoesCB1TOrKK9Qr4TM";
const supabase = createClient(SUPA_URL, SUPA_KEY);

// ── THE INSTRUMENT — editorial, monochrome base, semantic lean color ──
const LIGHT = {
  bg:"#F4F2EC", surface:"#ECE9E0", card:"#FBFAF6", border:"#DDD9CF",
  divider:"#E6E2D8", text:"#18171C", sub:"#54524C", muted:"#8C897F",
  orange:"#B14A40", accent:"#B14A40", accentSoft:"#F1E2DD",
  left:"#3E6FA8", right:"#B14A40", center:"#BF8A3C", breaking:"#B14A40",
};
const DARK = {
  bg:"#121317", surface:"#1A1B20", card:"#1E2026", border:"#2C2E36",
  divider:"#262830", text:"#ECEDF0", sub:"#A8A9B0", muted:"#74767E",
  orange:"#C75F54", accent:"#C75F54", accentSoft:"#2A1715",
  left:"#5A93C4", right:"#C75F54", center:"#D2A056", breaking:"#C75F54",
};
// C is set dynamically — see App component
// C is a mutable ref — components always read the latest theme
const C = { ...LIGHT };

const F = {
  display: "'Archivo', -apple-system, 'Helvetica Neue', sans-serif",
  text:    "'Archivo', -apple-system, 'Helvetica Neue', sans-serif",
  mono:    "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  serif:   "'Newsreader', Georgia, 'Times New Roman', serif",
};

// Solid card style — no glass, no blur
const card = () => ({
  background: C.card,
  border: `1px solid ${C.border}`,
  boxShadow: "none",
});

// Solid button style
const solidBtn = (active) => ({
  background: active ? "rgba(0,0,0,0.08)" : C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 980,
  color: active ? C.text : C.muted,
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
  fontFamily: F.text,
  transition: "all 0.15s",
});

// For backwards compat with glass() calls — just return solid card style
const glass = () => card();
const glassBtn = solidBtn;

const ARTICLES = [];

const CATS = ["All","Politics","World","Tech","Business","Science","Sports","Music","Health","Uplifting"];
const NAV  = [
  {id:"feed",       label:"Feed"},
  {id:"map",        label:"Map"},
  {id:"balance",    label:"Balance"},
  {id:"dna",        label:"DNA"},
  {id:"profile",    label:"Profile"},
];

const JOURNALISTS = {
  "Reuters":          { outlet:"Reuters",          beat:"World News, Politics, Finance",      trustScore:91, accuracyScore:94, leanHistory:{left:8,center:78,right:14},  corrections:3,  retractions:0, articlesThisYear:12400, bio:"Reuters is an international news organisation owned by Thomson Reuters. Its editorial standards require independent verification before publication.", notableWork:"Breaking coverage of major geopolitical events with verified sourcing.", verified:true },
  "Financial Times":  { outlet:"Financial Times",  beat:"Business, Economics, Policy",        trustScore:88, accuracyScore:91, leanHistory:{left:18,center:68,right:14}, corrections:5,  retractions:1, articlesThisYear:6800,  bio:"The Financial Times is a British daily newspaper focused on business and economic news. Known for rigorous financial reporting.", notableWork:"In-depth investigative reporting on global financial markets.", verified:true },
  "The Atlantic":     { outlet:"The Atlantic",     beat:"Politics, Culture, Ideas",           trustScore:74, accuracyScore:79, leanHistory:{left:62,center:30,right:8},  corrections:9,  retractions:1, articlesThisYear:2100,  bio:"The Atlantic is an American magazine covering news, politics, and cultural issues. Known for long-form journalism and opinion writing.", notableWork:"Award-winning long-form features on American political and social issues.", verified:true },
  "National Review":  { outlet:"National Review",  beat:"Politics, Conservative Commentary", trustScore:68, accuracyScore:72, leanHistory:{left:4,center:22,right:74},  corrections:11, retractions:2, articlesThisYear:1800,  bio:"National Review is an American conservative opinion magazine founded by William F. Buckley Jr. in 1955.", notableWork:"Consistent conservative policy analysis and political commentary.", verified:true },
  "NEJM":             { outlet:"NEJM",             beat:"Medicine, Clinical Research",        trustScore:98, accuracyScore:99, leanHistory:{left:5,center:92,right:3},   corrections:2,  retractions:0, articlesThisYear:890,   bio:"The New England Journal of Medicine is a weekly peer-reviewed medical journal, considered among the most prestigious in the world.", notableWork:"Landmark clinical trial publications shaping modern medical practice.", verified:true },
  "Chicago Tribune":  { outlet:"Chicago Tribune",  beat:"Local News, Politics, Culture",      trustScore:82, accuracyScore:85, leanHistory:{left:22,center:60,right:18}, corrections:7,  retractions:1, articlesThisYear:4200,  bio:"The Chicago Tribune is a daily newspaper based in Chicago, Illinois. Founded in 1847, it is one of the largest newspapers in the US.", notableWork:"Pulitzer Prize-winning local investigative journalism.", verified:true },
  "LA Times":         { outlet:"LA Times",         beat:"West Coast, Politics, Entertainment",trustScore:79, accuracyScore:83, leanHistory:{left:38,center:50,right:12}, corrections:10, retractions:2, articlesThisYear:5600,  bio:"The Los Angeles Times is a daily newspaper based in El Segundo, California. One of the largest metropolitan newspapers in the US.", notableWork:"Award-winning coverage of California politics and entertainment industry.", verified:true },
  "WSJ":              { outlet:"WSJ",              beat:"Business, Finance, Politics",        trustScore:85, accuracyScore:89, leanHistory:{left:10,center:55,right:35}, corrections:6,  retractions:1, articlesThisYear:8900,  bio:"The Wall Street Journal is a US business-focused daily newspaper. Its news reporting is broadly centrist while its opinion section leans right.", notableWork:"Definitive reporting on Wall Street, corporate America, and economic policy.", verified:true },
};

function leanColor(l){ return l==="left"?C.left:l==="right"?C.right:C.center; }

// AllSides Media Bias ratings — sourced from allsides.com/media-bias/media-bias-chart
// left = Left / Lean Left,  center = Center,  right = Right / Lean Right
const ALLSIDES_BIAS = {
  // LEFT
  "The New York Times": "left", "NYT": "left",
  "Washington Post": "left", "The Washington Post": "left",
  "The Guardian": "left", "Guardian": "left",
  "MSNBC": "left", "HuffPost": "left", "Huffington Post": "left",
  "Vox": "left", "Mother Jones": "left", "The Nation": "left",
  "Slate": "left", "BuzzFeed News": "left", "BuzzFeed": "left",
  "NPR": "left", "PBS": "left", "The Atlantic": "left",
  "CNN": "left", "New Yorker": "left", "The New Yorker": "left",
  "Vice": "left", "Vice News": "left", "Salon": "left",
  "Politico": "left", "ProPublica": "left", "Intercept": "left",
  "Democracy Now": "left", "Teen Vogue": "left",
  "LA Times": "left", "Los Angeles Times": "left",
  "Rolling Stone": "left", "Jacobin": "left",
  "Al Jazeera": "left", "Al Jazeera English": "left",
  "Independent": "left", "The Independent": "left",
  // CENTER
  "Reuters": "center", "Associated Press": "center", "AP": "center",
  "BBC": "center", "BBC News": "center",
  "The Hill": "center", "Hill": "center",
  "C-SPAN": "center", "PBS NewsHour": "center",
  "Christian Science Monitor": "center",
  "Financial Times": "center", "The Economist": "center",
  "Bloomberg": "center", "Axios": "center",
  "Newsweek": "center", "Time": "center", "TIME": "center",
  "USA Today": "center", "Chicago Tribune": "center",
  "The Wall Street Journal": "center", "WSJ": "center",
  "MarketWatch": "center", "Foreign Affairs": "center",
  "Defense One": "center", "Politifact": "center",
  "FactCheck.org": "center", "Snopes": "center",
  "Weather.com": "center", "Science": "center",
  "Nature": "center", "NEJM": "center",
  "ABC News": "center", "CBS News": "center", "NBC News": "center",
  // RIGHT
  "Fox News": "right", "Fox": "right",
  "Wall Street Journal Opinion": "right",
  "New York Post": "right", "NY Post": "right",
  "Breitbart": "right", "Daily Wire": "right",
  "National Review": "right", "The Federalist": "right",
  "Townhall": "right", "Daily Caller": "right",
  "Washington Times": "right", "Washington Examiner": "right",
  "Newsmax": "right", "OAN": "right", "One America News": "right",
  "The Blaze": "right", "PJ Media": "right",
  "Western Journal": "right", "Epoch Times": "right",
  "Daily Signal": "right", "American Spectator": "right",
  "Reason": "right", "New York Sun": "right",
  "The Dispatch": "right", "Commentary": "right",
};

function getSourceLean(sourceName) {
  if (!sourceName) return null;
  // Exact match first
  if (ALLSIDES_BIAS[sourceName]) return ALLSIDES_BIAS[sourceName];
  // Partial match (e.g. "BBC News" matches "BBC")
  const lower = sourceName.toLowerCase();
  for (const [key, val] of Object.entries(ALLSIDES_BIAS)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) return val;
  }
  return null;
}

// ── Image guarantee: a story always has something visual ──
// Maps a string to a stable hue so each source/category gets a consistent placeholder.
function hashHue(str="") {
  let h = 0;
  for (let i=0;i<str.length;i++) h = (h*31 + str.charCodeAt(i)) % 360;
  return h;
}
// Returns a CSS background for the fallback placeholder — warm, on-brand, deterministic.
function placeholderBg(seed="") {
  const hue = hashHue(seed);
  // keep within warm/earthy band so it always reads on-brand
  const h1 = 20 + (hue % 35);   // terracotta-ish
  const h2 = 180 + (hue % 40);  // muted teal-ish for contrast
  return `linear-gradient(135deg, hsl(${h1},42%,62%) 0%, hsl(${h2},22%,42%) 100%)`;
}

// ── Bias spectrum position: left=12%, center=50%, right=88% ──
function leanPosition(l){ return l==="left" ? "16%" : l==="right" ? "84%" : "50%"; }
function leanLabel(l){ return l==="left" ? "Left coverage" : l==="right" ? "Right coverage" : "Center coverage"; }

// Full bias spectrum bar — the signature element
function BiasSpectrum({ lean, compact=false }) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:compact?4:5}}>
      <div style={{position:"relative",height:compact?5:6,borderRadius:4,
        background:`linear-gradient(to right, ${C.left} 0%, ${C.center} 50%, ${C.right} 100%)`}}>
        <div style={{position:"absolute",top:"50%",left:leanPosition(lean),
          width:compact?10:12,height:compact?10:12,borderRadius:"50%",
          background:"#fff",border:`2.5px solid ${C.text}`,transform:"translate(-50%,-50%)"}}/>
      </div>
      {!compact && (
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontFamily:F.mono,fontSize:8.5,color:C.muted,letterSpacing:"0.1em",textTransform:"uppercase"}}>Left</span>
          <span style={{fontFamily:F.mono,fontSize:8.5,fontWeight:700,color:leanColor(lean),letterSpacing:"0.08em",textTransform:"uppercase"}}>{leanLabel(lean)}</span>
          <span style={{fontFamily:F.mono,fontSize:8.5,color:C.muted,letterSpacing:"0.1em",textTransform:"uppercase"}}>Right</span>
        </div>
      )}
    </div>
  );
}

// Reusable story image with guaranteed fallback
function StoryImage({ src, seed, height, radius=0, label }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div style={{height,borderRadius:radius,background:placeholderBg(seed),
        display:"flex",alignItems:"flex-end",padding:10,position:"relative",overflow:"hidden"}}>
        {label && <span style={{fontFamily:F.mono,fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.9)",
          letterSpacing:"0.08em",textTransform:"uppercase"}}>{label}</span>}
      </div>
    );
  }
  return (
    <div style={{height,borderRadius:radius,overflow:"hidden"}}>
      <img src={src} alt="" onError={()=>setFailed(true)}
        style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
    </div>
  );
}


// Decode HTML entities in article text (e.g. &#8216; -> ' , &amp; -> &)
function decodeHTML(str) {
  if (!str) return "";
  return str
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ").replace(/&ndash;/g, "–").replace(/&mdash;/g, "—")
    .replace(/&lsquo;/g, "\u2018").replace(/&rsquo;/g, "\u2019")
    .replace(/&ldquo;/g, "\u201c").replace(/&rdquo;/g, "\u201d")
    .replace(/&hellip;/g, "…").replace(/&bull;/g, "•")
    .replace(/<[^>]+>/g, "").trim();
}

// Format ISO date → "Mar 3, 2026 · 2:14 PM"
function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })
      + " · "
      + d.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true });
  } catch { return null; }
}

function callClaude(prompt, maxTokens=8000) {
  return fetch("https://clarion-proxy.vercel.app/api/claude", {
    method:"POST", headers:{"Content-Type":"application/json"},
    body: JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:maxTokens,
      system:"Return ONLY valid JSON. No markdown, no backticks, no preamble.",
      messages:[{role:"user",content:prompt}] })
  }).then(r=>r.json()).then(d=>d.content?.[0]?.text||"");
}

// ─────────────────────────────────────────────────────────────────
// SPINNER
// ─────────────────────────────────────────────────────────────────
function Spinner({ size=36, color=C.orange }) {
  return (
    <>
      <style>{`
        @keyframes clarion-sun-spin { to { transform: rotate(360deg); } }
        .clarion-sun { animation: clarion-sun-spin 2s linear infinite; transform-origin: center; }
      `}</style>
      <svg className="clarion-sun" width={size} height={size} viewBox="0 0 60 60" fill="none" style={{flexShrink:0}}>
        {/* Circle */}
        <circle cx="30" cy="30" r="9" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none"/>
        {/* Rays — hand-drawn style, varying lengths and slight rotation like the image */}
        <line x1="30" y1="6"  x2="30" y2="14" stroke={color} strokeWidth="2.8" strokeLinecap="round"/>
        <line x1="30" y1="46" x2="30" y2="54" stroke={color} strokeWidth="2.8" strokeLinecap="round"/>
        <line x1="6"  y1="30" x2="14" y2="30" stroke={color} strokeWidth="2.8" strokeLinecap="round"/>
        <line x1="46" y1="30" x2="54" y2="30" stroke={color} strokeWidth="2.8" strokeLinecap="round"/>
        {/* Diagonal rays — shorter, dashed feel */}
        <line x1="13" y1="13" x2="19" y2="19" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
        <line x1="41" y1="41" x2="47" y2="47" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
        <line x1="47" y1="13" x2="41" y2="19" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
        <line x1="13" y1="47" x2="19" y2="41" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
        {/* Short accent dashes between rays */}
        <line x1="21" y1="8"  x2="23" y2="11" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="39" y1="8"  x2="37" y2="11" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="52" y1="21" x2="49" y2="23" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="52" y1="39" x2="49" y2="37" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="39" y1="52" x2="37" y2="49" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="21" y1="52" x2="23" y2="49" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="8"  y1="39" x2="11" y2="37" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="8"  y1="21" x2="11" y2="23" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────
// CLARION WORDMARK — SVG hand-drawn style (based on founder sketch)
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// CLARION WORDMARK — Newsreader serif nameplate (no tagline; tagline lives in footer)
// ─────────────────────────────────────────────────────────────────
function ClarionLogoFull({ height=34, color }) {
  return (
    <div style={{
      fontFamily:F.serif, fontSize:height, fontWeight:700, color: color || C.text,
      letterSpacing:"-0.01em", lineHeight:0.9, userSelect:"none",
    }}>
      Clarion.
    </div>
  );
}

// "Loud & Clear" — quiet payoff at the very bottom of the feed
function LoudClearFooter() {
  return (
    <div style={{padding:"36px 18px 30px",textAlign:"center"}}>
      <div style={{height:1,background:C.border,marginBottom:22}}/>
      <div style={{fontFamily:F.serif,fontSize:26,fontWeight:700,color:C.text,letterSpacing:"-0.01em",lineHeight:1}}>Clarion.</div>
      <div style={{fontFamily:F.mono,fontSize:9,fontWeight:600,color:C.accent,letterSpacing:"0.28em",textTransform:"uppercase",marginTop:8}}>Loud &amp; Clear</div>
      <div style={{fontFamily:F.mono,fontSize:8.5,color:C.muted,letterSpacing:"0.06em",marginTop:14}}>theclarion.app · every angle, one place</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SHEET WRAPPER (bottom sheet)
// ─────────────────────────────────────────────────────────────────
function Sheet({ onClose, children }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.22)", zIndex:500, display:"flex", alignItems:"flex-end" }} onClick={onClose}>
      <div style={{ background:C.bg, width:"100%", maxWidth:560, margin:"0 auto", borderRadius:"20px 20px 0 0", padding:"20px 24px 52px", boxShadow:"0 -4px 48px rgba(0,0,0,0.10)", maxHeight:"88vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
        <div style={{ width:40, height:5, background:"rgba(0,0,0,0.12)", borderRadius:99, margin:"0 auto 22px" }}/>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TRUST METER
// ─────────────────────────────────────────────────────────────────
function TrustMeter({ score, size=72 }) {
  const r = size * 0.38, cx = size / 2;
  const circ = Math.PI * r;
  const color = score >= 85 ? "#3A9E6A" : score >= 65 ? "#5CB87A" : "#80C994";
  return (
    <svg width={size} height={size * 0.6} viewBox={`0 0 ${size} ${size * 0.6}`}>
      <path d={`M ${cx-r} ${size*.55} A ${r} ${r} 0 0 1 ${cx+r} ${size*.55}`} fill="none" stroke={C.divider} strokeWidth={size*.09} strokeLinecap="round"/>
      <path d={`M ${cx-r} ${size*.55} A ${r} ${r} 0 0 1 ${cx+r} ${size*.55}`} fill="none" stroke={color} strokeWidth={size*.09} strokeLinecap="round" strokeDasharray={`${(score/100)*circ} ${circ}`}/>
      <text x={cx} y={size*.46} textAnchor="middle" fontFamily={F.display} fontSize={size*.27} fontWeight={700} fill={color}>{score}</text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────
// ARTICLE CARD
// ─────────────────────────────────────────────────────────────────
function ArticleCard({ a, onRead, bookmarks, setBookmarks, setVerifying, onJournalist, isLead, isGrid, onCompare, onBookmarkSync }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [imgOk, setImgOk] = useState(!!a.image);
  const saved = bookmarks.includes(a.id);
  const lc = leanColor(a.lean);
  const dateStr = formatDate(a.publishedAt);
  const imgH = isLead ? 210 : isGrid ? 110 : 150;

  // Update imgOk if article image prop changes
  useEffect(() => { setImgOk(!!a.image); }, [a.image]);

  const handleImgError = () => setImgOk(false);

  const Fallback = () => (
    <div style={{ height:imgH, flexShrink:0, background:`linear-gradient(135deg,${lc}18,${lc}08)`, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:4 }}>
      <div style={{ fontSize: isGrid ? 22 : 30, fontWeight:700, color:lc, opacity:0.6, fontFamily:"'Times New Roman',serif", lineHeight:1 }}>{(a.source||"?")[0].toUpperCase()}</div>
      <div style={{ fontSize:10, color:lc, opacity:0.45, fontFamily:F.text }}>{a.source}</div>
    </div>
  );

  return (
    <div style={{ borderRadius:20, overflow:"hidden", ...glass(0.68), boxShadow:"none", display:"flex", flexDirection:"column" }}>

      {/* ── COLLAPSED ── */}
      <div onClick={()=>{ setOpen(v=>!v); onRead(a.id); }} style={{ cursor:"pointer", flex:1, display:"flex", flexDirection:"column" }}>

        {/* Image */}
        {a.image && imgOk ? (
          <div style={{ height:imgH, flexShrink:0, overflow:"hidden", background:C.surface }}>
            <img src={a.image} alt=""
              style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
              onError={handleImgError}/>
          </div>
        ) : (
          <Fallback/>
        )}

        {/* Text */}
        <div style={{ padding: isGrid ? "10px 12px 12px" : "14px 16px 16px", flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5, overflow:"hidden" }}>
            {a.breaking && <span style={{ fontSize:8, fontWeight:700, color:"#fff", background:C.breaking, borderRadius:4, padding:"1px 5px", fontFamily:F.text, flexShrink:0 }}>LIVE</span>}
            <span onClick={e=>{ e.stopPropagation(); onJournalist(a.source); }}
              style={{ fontSize:10, fontWeight:600, color:C.accent, fontFamily:F.text, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap", maxWidth:"55%", overflow:"hidden", textOverflow:"ellipsis" }}
            >{a.source}</span>
            <span style={{ fontSize:10, color:"rgba(0,0,0,0.18)", flexShrink:0 }}>·</span>
            <span style={{ fontSize:10, color:C.muted, fontFamily:F.text, flexShrink:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{dateStr || a.time}</span>
            <span style={{ marginLeft:"auto", fontSize:9, color:lc, background:lc+"18", borderRadius:20, padding:"1px 6px", fontFamily:F.text, flexShrink:0, fontWeight:600 }}>
              {a.lean==="left"?"◀ L":a.lean==="right"?"▶ R":"● C"}
            </span>
          </div>
          <p style={{
            fontFamily:F.display, margin:0, color:C.text, lineHeight:1.35, letterSpacing:"-0.02em",
            fontSize: isLead ? 18 : isGrid ? 13 : 15,
            fontWeight: isLead ? 700 : 600,
            display:"-webkit-box", WebkitLineClamp: isGrid ? 3 : 5, WebkitBoxOrient:"vertical", overflow:"hidden",
          }}>{decodeHTML(a.headline)}</p>
          {isLead && a.summary && (
            <p style={{ fontFamily:F.text, fontSize:13, color:C.muted, margin:"6px 0 0", lineHeight:1.6,
              display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
              {decodeHTML(a.summary)}
            </p>
          )}
        </div>
      </div>

      {/* ── EXPANDED ── */}
      {open && (
        <div style={{ padding:"0 16px 16px" }}>
          <div style={{ height:1, background:"rgba(0,0,0,0.06)", marginBottom:12 }}/>
          <p style={{ fontFamily:F.text, fontSize:14, color:C.sub, lineHeight:1.75, margin:"0 0 12px" }}>{decodeHTML(a.summary)}</p>
          {dateStr && <p style={{ fontFamily:F.text, fontSize:11, color:C.muted, margin:"0 0 12px" }}>Published {dateStr}</p>}
          <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:12 }}>
            {a.url && (
              <button onClick={()=>window.open(a.url,"_blank","noopener,noreferrer")} style={{
                ...glassBtn(false), padding:"7px 14px", fontSize:12, fontWeight:600,
              }}>Read ↗</button>
            )}
            <button onClick={()=>{ if(navigator.vibrate) navigator.vibrate(saved?6:14); const adding=!saved; setBookmarks(v=>adding?[...v,a.id]:v.filter(x=>x!==a.id)); if(onBookmarkSync) onBookmarkSync(a, adding); }} style={{
              ...glassBtn(saved), padding:"7px 12px", fontSize:12,
            }}>{saved?"Saved" : "Save"}</button>
            <button onClick={()=>setVerifying(a)} style={{
              ...glassBtn(false), padding:"7px 12px", fontSize:12,
            }}>Fact Check</button>
            {onCompare && <button onClick={e=>{ e.stopPropagation(); if(navigator.vibrate) navigator.vibrate(10); onCompare(a); }} style={{
              ...glassBtn(false), padding:"7px 12px", fontSize:12,
            }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline-block",verticalAlign:"middle",marginRight:4}}><path d="M12 3v18M5 8l7-5 7 5M5 16l7 5 7-5"/></svg>Compare</button>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <input value={comment} onChange={e=>setComment(e.target.value)} placeholder="Comment…"
              onKeyDown={e=>{ if(e.key==="Enter"&&comment.trim()){setComments(v=>[...v,comment]);setComment("");} }}
              style={{ flex:1, ...glass(0.5), border:"none", borderRadius:10, padding:"8px 12px", fontSize:13, color:C.text, outline:"none", fontFamily:F.text }}/>
            <button onClick={()=>{ if(comment.trim()){setComments(v=>[...v,comment]);setComment("");} }}
              style={{ ...glassBtn(false), padding:"8px 14px", fontSize:13, borderRadius:10 }}>↑</button>
          </div>
          {comments.map((c,i)=>(
            <p key={i} style={{ fontFamily:F.text, fontSize:13, color:C.sub, margin:"8px 0 0", paddingLeft:10, borderLeft:`2px solid ${C.divider}` }}>{c}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// BIAS GAUGE
// ─────────────────────────────────────────────────────────────────
function BiasGauge({ history, allArticles }) {
  const counts = history.reduce((a,id)=>{
    const art = allArticles.find(x=>x.id===id);
    if(art) a[art.lean]=(a[art.lean]||0)+1;
    return a;
  },{left:0,center:0,right:0});
  const total=(counts.left||0)+(counts.center||0)+(counts.right||0)||1;
  const hasData = history.length > 0;
  const score=((counts.right||0)-(counts.left||0))/total;
  const pct=((score+1)/2)*100;
  const label = score < -0.3 ? "Leaning Left" : score > 0.3 ? "Leaning Right" : "Well Balanced";
  const labelColor = score < -0.3 ? C.left : score > 0.3 ? C.right : C.center;
  const note = score < -0.3 ? "Try mixing in some center or right-leaning sources."
             : score >  0.3 ? "Try mixing in some center or left-leaning sources."
             : "You're reading a healthy mix across the spectrum.";
  return (
    <div style={{marginBottom:28}}>
      <div style={{marginBottom:20}}>
        <div style={{position:"relative", height:8, borderRadius:99, background:`linear-gradient(to right,${C.left},${C.center},${C.right})`, marginBottom:8}}>
          <div style={{
            position:"absolute", top:"50%", transform:"translate(-50%,-50%)",
            width:22, height:22, borderRadius:"50%",
            background:C.bg, border:`3px solid ${labelColor}`,
            boxShadow:`0 2px 10px ${labelColor}55`,
            left:`${pct}%`, transition:"left 0.7s cubic-bezier(.34,1.56,.64,1)",
          }}/>
        </div>
        <div style={{display:"flex", justifyContent:"space-between", fontFamily:F.text, fontSize:10, fontWeight:600, letterSpacing:"0.08em"}}>
          <span style={{color:C.left}}>LEFT</span>
          <span style={{color:C.center}}>CENTER</span>
          <span style={{color:C.right}}>RIGHT</span>
        </div>
      </div>
      <div style={{background:C.surface, borderRadius:14, padding:"16px 18px", display:"flex", alignItems:"center", gap:14}}>
        <div style={{width:10, height:10, borderRadius:"50%", background:labelColor, flexShrink:0, boxShadow:`0 0 0 3px ${labelColor}22`}}/>
        <div style={{flex:1}}>
          <p style={{fontFamily:F.display, fontSize:15, fontWeight:700, color:C.text, margin:"0 0 2px"}}>{hasData ? label : "No data yet"}</p>
          <p style={{fontFamily:F.text, fontSize:12, color:C.muted, margin:0, lineHeight:1.5}}>
            {hasData ? note : "Read stories in your feed to start tracking."}
          </p>
        </div>
      </div>
      {hasData && (
        <div style={{display:"flex", gap:8, marginTop:10}}>
          {[["Left",counts.left||0,C.left],["Center",counts.center||0,C.center],["Right",counts.right||0,C.right]].map(([l,v,c])=>(
            <div key={l} style={{flex:1, background:C.surface, borderRadius:12, padding:"12px 8px", textAlign:"center"}}>
              <div style={{fontFamily:F.display, fontSize:22, fontWeight:700, color:c, letterSpacing:"-0.02em"}}>{v}</div>
              <div style={{fontFamily:F.text, fontSize:10, color:C.muted, marginTop:2, letterSpacing:"0.05em"}}>{l.toUpperCase()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// HEATMAP — Mapbox interactive map
// ─────────────────────────────────────────────────────────────────
// Token injected at build time via REACT_APP_MAPBOX_TOKEN env var
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "pk.eyJ1IjoiY2xhcmlvbjEzIiwiYSI6ImNtbWNxMmxuOTA4dnQycXE1a2h0OWV1ZHUifQ.Vix6Aa28lbFYADZP1uOkFA";

const CITIES = [
  // US Cities
  {name:"New York",lat:40.7128,lng:-74.0060},
  {name:"Los Angeles",lat:34.0522,lng:-118.2437},
  {name:"Chicago",lat:41.8781,lng:-87.6298},
  {name:"Houston",lat:29.7604,lng:-95.3698},
  {name:"Washington D.C.",lat:38.9072,lng:-77.0369},
  {name:"San Francisco",lat:37.7749,lng:-122.4194},
  {name:"Seattle",lat:47.6062,lng:-122.3321},
  {name:"Miami",lat:25.7617,lng:-80.1918},
  {name:"Boston",lat:42.3601,lng:-71.0589},
  {name:"Atlanta",lat:33.7490,lng:-84.3880},
  {name:"Dallas",lat:32.7767,lng:-96.7970},
  {name:"Denver",lat:39.7392,lng:-104.9903},
  {name:"Las Vegas",lat:36.1699,lng:-115.1398},
  {name:"Austin",lat:30.2672,lng:-97.7431},
  {name:"Phoenix",lat:33.4484,lng:-112.0740},
  {name:"Nashville",lat:36.1627,lng:-86.7816},
  {name:"Portland",lat:45.5051,lng:-122.6750},
  {name:"Minneapolis",lat:44.9778,lng:-93.2650},
  {name:"Detroit",lat:42.3314,lng:-83.0458},
  {name:"Philadelphia",lat:39.9526,lng:-75.1652},
  {name:"San Diego",lat:32.7157,lng:-117.1611},
  {name:"Tampa",lat:27.9506,lng:-82.4572},
  {name:"Baltimore",lat:39.2904,lng:-76.6122},
  {name:"Salt Lake City",lat:40.7608,lng:-111.8910},
  {name:"New Orleans",lat:29.9511,lng:-90.0715},
  // Europe
  {name:"London",lat:51.5074,lng:-0.1278},
  {name:"Paris",lat:48.8566,lng:2.3522},
  {name:"Berlin",lat:52.5200,lng:13.4050},
  {name:"Moscow",lat:55.7558,lng:37.6173},
  {name:"Kyiv",lat:50.4501,lng:30.5234},
  {name:"Brussels",lat:50.8503,lng:4.3517},
  {name:"Rome",lat:41.9028,lng:12.4964},
  {name:"Madrid",lat:40.4168,lng:-3.7038},
  {name:"Amsterdam",lat:52.3676,lng:4.9041},
  {name:"Warsaw",lat:52.2297,lng:21.0122},
  {name:"Stockholm",lat:59.3293,lng:18.0686},
  {name:"Zurich",lat:47.3769,lng:8.5417},
  {name:"Athens",lat:37.9838,lng:23.7275},
  {name:"Vienna",lat:48.2082,lng:16.3738},
  // Middle East & Africa
  {name:"Tel Aviv",lat:32.0853,lng:34.7818},
  {name:"Gaza",lat:31.5017,lng:34.4668},
  {name:"Beirut",lat:33.8938,lng:35.5018},
  {name:"Tehran",lat:35.6892,lng:51.3890},
  {name:"Riyadh",lat:24.7136,lng:46.6753},
  {name:"Dubai",lat:25.2048,lng:55.2708},
  {name:"Istanbul",lat:41.0082,lng:28.9784},
  {name:"Cairo",lat:30.0444,lng:31.2357},
  {name:"Nairobi",lat:-1.2921,lng:36.8219},
  {name:"Lagos",lat:6.5244,lng:3.3792},
  {name:"Johannesburg",lat:-26.2041,lng:28.0473},
  // Asia Pacific
  {name:"Beijing",lat:39.9042,lng:116.4074},
  {name:"Shanghai",lat:31.2304,lng:121.4737},
  {name:"Hong Kong",lat:22.3193,lng:114.1694},
  {name:"Tokyo",lat:35.6762,lng:139.6503},
  {name:"Seoul",lat:37.5665,lng:126.9780},
  {name:"Taipei",lat:25.0330,lng:121.5654},
  {name:"Singapore",lat:1.3521,lng:103.8198},
  {name:"Mumbai",lat:19.0760,lng:72.8777},
  {name:"New Delhi",lat:28.6139,lng:77.2090},
  {name:"Islamabad",lat:33.6844,lng:73.0479},
  {name:"Kabul",lat:34.5553,lng:69.2075},
  {name:"Sydney",lat:-33.8688,lng:151.2093},
  {name:"Canberra",lat:-35.2809,lng:149.1300},
  // Americas
  {name:"Ottawa",lat:45.4215,lng:-75.6972},
  {name:"Toronto",lat:43.6532,lng:-79.3832},
  {name:"Vancouver",lat:49.2827,lng:-123.1207},
  {name:"Mexico City",lat:19.4326,lng:-99.1332},
  {name:"Havana",lat:23.1136,lng:-82.3666},
  {name:"Caracas",lat:10.4806,lng:-66.9036},
  {name:"Bogota",lat:4.7110,lng:-74.0721},
  {name:"Brasilia",lat:-15.8267,lng:-47.9218},
  {name:"Buenos Aires",lat:-34.6037,lng:-58.3816},
  {name:"Santiago",lat:-33.4489,lng:-70.6693},
  // More Americas
  {name:"Lima",lat:-12.0464,lng:-77.0428},
  {name:"Quito",lat:-0.1807,lng:-78.4678},
  {name:"Montevideo",lat:-34.9011,lng:-56.1645},
  {name:"Asuncion",lat:-25.2867,lng:-57.6470},
  {name:"La Paz",lat:-16.4897,lng:-68.1193},
  // More Europe
  {name:"Lisbon",lat:38.7169,lng:-9.1399},
  {name:"Prague",lat:50.0755,lng:14.4378},
  {name:"Budapest",lat:47.4979,lng:19.0402},
  {name:"Bucharest",lat:44.4268,lng:26.1025},
  {name:"Sofia",lat:42.6977,lng:23.3219},
  {name:"Belgrade",lat:44.8176,lng:20.4633},
  // More Middle East
  {name:"Baghdad",lat:33.3152,lng:44.3661},
  {name:"Amman",lat:31.9566,lng:35.9456},
  {name:"Damascus",lat:33.5138,lng:36.2765},
  {name:"Doha",lat:25.2854,lng:51.5310},
  {name:"Kuwait City",lat:29.3759,lng:47.9774},
  {name:"Muscat",lat:23.5880,lng:58.3829},
  {name:"Sanaa",lat:15.5527,lng:44.2075},
  // More Africa
  {name:"Accra",lat:5.6037,lng:-0.1870},
  {name:"Addis Ababa",lat:9.0249,lng:38.7469},
  {name:"Dakar",lat:14.7167,lng:-17.4677},
  {name:"Khartoum",lat:15.5007,lng:32.5599},
  {name:"Kinshasa",lat:-4.4419,lng:15.2663},
  {name:"Luanda",lat:-8.8368,lng:13.2343},
  {name:"Dar es Salaam",lat:-6.7924,lng:39.2083},
  // More Asia
  {name:"Bangkok",lat:13.7563,lng:100.5018},
  {name:"Jakarta",lat:-6.2088,lng:106.8456},
  {name:"Kuala Lumpur",lat:3.1390,lng:101.6869},
  {name:"Manila",lat:14.5995,lng:120.9842},
  {name:"Dhaka",lat:23.8103,lng:90.4125},
  {name:"Colombo",lat:6.9271,lng:79.8612},
  {name:"Kathmandu",lat:27.7172,lng:85.3240},
  {name:"Yangon",lat:16.8661,lng:96.1951},
  {name:"Hanoi",lat:21.0285,lng:105.8542},
  {name:"Ho Chi Minh City",lat:10.8231,lng:106.6297},
  {name:"Ulaanbaatar",lat:47.8864,lng:106.9057},
  {name:"Tashkent",lat:41.2995,lng:69.2401},
  {name:"Almaty",lat:43.2220,lng:76.8512},
  {name:"Baku",lat:40.4093,lng:49.8671},
  {name:"Yerevan",lat:40.1872,lng:44.5152},
  {name:"Tbilisi",lat:41.6938,lng:44.8015},
  // More Oceania
  {name:"Melbourne",lat:-37.8136,lng:144.9631},
  {name:"Auckland",lat:-36.8509,lng:174.7645},
];

// Keyword map: location name -> keywords that should map to it
const CITY_KEYWORDS = {
  // US
  "New York":         ["new york","nyc","manhattan","brooklyn","bronx","queens","wall street","times square","new york city"],
  "Los Angeles":      ["los angeles","l.a.","hollywood","beverly hills","compton","dodgers","lakers"],
  "Chicago":          ["chicago","windy city","wrigley","cubs","bears","bulls"],
  "Houston":          ["houston","harris county","nasa","texans","astros"],
  "Washington D.C.":  ["washington d.c","washington dc","white house","congress","senate","capitol hill","pentagon","state department","supreme court","federal government","federal reserve","oval office","cia","fbi","treasury department","justice department","defense department"],
  "San Francisco":    ["san francisco","sf bay","silicon valley","palo alto","menlo park","sunnyvale","santa clara","oakland","berkeley","golden gate","big tech","apple inc","google","meta ","openai","anthropic"],
  "Seattle":          ["seattle","puget sound","seahawks","mariners","amazon hq","microsoft campus"],
  "Miami":            ["miami","south beach","miami beach","dade county"],
  "Boston":           ["boston","cambridge ma","harvard","mit ","celtics","patriots","red sox"],
  "Atlanta":          ["atlanta","atl ","georgia capital","braves","falcons","hartsfield"],
  "Dallas":           ["dallas","fort worth","dfw","cowboys","rangers","mavericks"],
  "Denver":           ["denver","mile high","broncos","rockies","nuggets"],
  "Las Vegas":        ["las vegas","sin city","the strip","raiders","golden knights"],
  "Austin":           ["austin tx","sxsw","tesla texas","university of texas"],
  "Phoenix":          ["phoenix","scottsdale","tempe","suns","cardinals az","diamondbacks"],
  "Nashville":        ["nashville","country music","titans","predators","music city"],
  "Portland":         ["portland or","trail blazers","pdx"],
  "Minneapolis":      ["minneapolis","saint paul","twin cities","vikings","timberwolves"],
  "Detroit":          ["detroit","motor city","motown","lions","tigers","michigan"],
  "Philadelphia":     ["philadelphia","philly","eagles","phillies","76ers"],
  "San Diego":        ["san diego","padres"],
  "Tampa":            ["tampa","st. petersburg","buccaneers","tampa bay"],
  "Baltimore":        ["baltimore","ravens","orioles"],
  "Salt Lake City":   ["salt lake city","utah jazz"],
  "New Orleans":      ["new orleans","nola","saints new orleans","mardi gras"],
  // Europe
  "London":           ["london","uk ","united kingdom","england","britain","british","westminster","parliament uk","downing street","buckingham","bank of england","premier league","scotland","wales","northern ireland"],
  "Paris":            ["paris","france","french","macron","elysee","notre dame","louvre"],
  "Berlin":           ["berlin","germany","german","bundestag","bundesbank","munich","hamburg","frankfurt","volkswagen","bmw","mercedes"],
  "Moscow":           ["moscow","russia","russian","kremlin","putin","st petersburg ru","siberia","ukraine war","rosneft"],
  "Kyiv":             ["kyiv","kiev","ukraine","ukrainian","zelensky","donbas","zaporizhzhia","kharkiv","odessa","mariupol","kherson"],
  "Brussels":         ["brussels","belgium","european union","eu ","nato","european commission","european parliament"],
  "Rome":             ["rome","italy","italian","vatican","pope","milan","naples","sicily","mario draghi"],
  "Madrid":           ["madrid","spain","spanish","barcelona","catalonia","iberia"],
  "Amsterdam":        ["amsterdam","netherlands","dutch","holland","rotterdam"],
  "Warsaw":           ["warsaw","poland","polish","krakow"],
  "Stockholm":        ["stockholm","sweden","swedish","nordic","scandinavia","oslo","norway","denmark","copenhagen","helsinki","finland"],
  "Zurich":           ["zurich","switzerland","swiss","geneva","davos","imf","world economic forum"],
  "Athens":           ["athens","greece","greek","acropolis","mediterranean"],
  "Vienna":           ["vienna","austria","austrian","opec","iaea"],
  // Middle East & Africa
  "Tel Aviv":         ["tel aviv","israel","israeli","netanyahu","idf","jerusalem","west bank","haifa","iron dome"],
  "Gaza":             ["gaza","hamas","palestin","rafah","west bank","idf strike","ceasefire"],
  "Beirut":           ["beirut","lebanon","lebanese","hezbollah"],
  "Tehran":           ["tehran","iran","iranian","ayatollah","irgc","nuclear deal","persian"],
  "Riyadh":           ["riyadh","saudi arabia","saudi","mbs","aramco","opec","crown prince"],
  "Dubai":            ["dubai","uae","united arab emirates","abu dhabi","emirates airline"],
  "Istanbul":         ["istanbul","turkey","turkish","ankara","erdogan","bosphorus"],
  "Cairo":            ["cairo","egypt","egyptian","suez","al-sisi"],
  "Nairobi":          ["nairobi","kenya","kenyan","east africa"],
  "Lagos":            ["lagos","nigeria","nigerian","west africa","abuja"],
  "Johannesburg":     ["johannesburg","south africa","pretoria","cape town","african national congress","anc "],
  // Asia Pacific
  "Beijing":          ["beijing","china","chinese","xi jinping","ccp","communist party","pla ","xinjiang","tibet","taiwan strait","south china sea","huawei","alibaba","tencent","byd"],
  "Shanghai":         ["shanghai","yangtze","pudong"],
  "Hong Kong":        ["hong kong","hk ","carrie lam","john lee","national security law"],
  "Tokyo":            ["tokyo","japan","japanese","abe","kishida","nikkei","fujisan","osaka","hiroshima","nagasaki","kyoto"],
  "Seoul":            ["seoul","south korea","korean","north korea","kim jong","pyongyang","dmz","samsung","hyundai","kpop","bts "],
  "Taipei":           ["taipei","taiwan","taiwanese","tsai","dpp","kuomintang","strait","chip","tsmc","semiconductor"],
  "Singapore":        ["singapore","singaporean","asean","straits times"],
  "Mumbai":           ["mumbai","india","indian","modi","bjp","delhi","bangalore","hyderabad","chennai","kolkata","rupee","tata ","infosys","hindustan"],
  "New Delhi":        ["new delhi","india ","modi ","parliament india","supreme court india"],
  "Islamabad":        ["islamabad","pakistan","pakistani","imran khan","lahore","karachi","isi pak"],
  "Kabul":            ["kabul","afghanistan","afghan","taliban","kandahar"],
  "Sydney":           ["sydney","australia","australian","melbourne","queensland","aukus","reef","asx"],
  "Canberra":         ["canberra","australia government","albanese","australian parliament"],
  // Americas
  "Ottawa":           ["ottawa","canada","canadian","trudeau","liberal party canada","ontario","alberta","quebec","bank of canada"],
  "Toronto":          ["toronto","ontario","maple leafs","raptors","blue jays"],
  "Vancouver":        ["vancouver","british columbia","bc "],
  "Mexico City":      ["mexico city","mexico","mexican","cdmx","amlo","pemex","cartels","guadalajara","monterrey"],
  "Havana":           ["havana","cuba","cuban","castro","embargo"],
  "Caracas":          ["caracas","venezuela","venezuelan","maduro","chavez"],
  "Bogota":           ["bogota","colombia","colombian","medellin","farc","petro"],
  "Brasilia":         ["brasilia","brazil","brazilian","lula","bolsonaro","sao paulo","amazon rainforest","rio de janeiro"],
  "Buenos Aires":     ["buenos aires","argentina","argentine","milei","peronist","peso crisis"],
  "Santiago":         ["santiago","chile","chilean","boric"],
  // More Americas
  "Lima":             ["lima","peru","peruvian"],
  "Quito":            ["quito","ecuador","ecuadorian"],
  "Montevideo":       ["montevideo","uruguay","uruguayan"],
  "Asuncion":         ["asuncion","paraguay","paraguayan"],
  "La Paz":           ["la paz","bolivia","bolivian"],
  // More Europe
  "Lisbon":           ["lisbon","portugal","portuguese"],
  "Prague":           ["prague","czech","czechia"],
  "Budapest":         ["budapest","hungary","hungarian","orban"],
  "Bucharest":        ["bucharest","romania","romanian"],
  "Sofia":            ["sofia","bulgaria","bulgarian"],
  "Belgrade":         ["belgrade","serbia","serbian"],
  // More Middle East
  "Baghdad":          ["baghdad","iraq","iraqi"],
  "Amman":            ["amman","jordan","jordanian"],
  "Damascus":         ["damascus","syria","syrian","assad"],
  "Doha":             ["doha","qatar","qatari","al jazeera","world cup qatar"],
  "Kuwait City":      ["kuwait","kuwaiti"],
  "Muscat":           ["muscat","oman","omani"],
  "Sanaa":            ["sanaa","yemen","yemeni","houthi"],
  // More Africa
  "Accra":            ["accra","ghana","ghanaian"],
  "Addis Ababa":      ["addis ababa","ethiopia","ethiopian","african union"],
  "Dakar":            ["dakar","senegal","senegalese"],
  "Khartoum":         ["khartoum","sudan","sudanese"],
  "Kinshasa":         ["kinshasa","congo","drc","democratic republic"],
  "Luanda":           ["luanda","angola","angolan"],
  "Dar es Salaam":    ["dar es salaam","tanzania","tanzanian"],
  // More Asia
  "Bangkok":          ["bangkok","thailand","thai"],
  "Jakarta":          ["jakarta","indonesia","indonesian"],
  "Kuala Lumpur":     ["kuala lumpur","malaysia","malaysian"],
  "Manila":           ["manila","philippines","philippine","marcos"],
  "Dhaka":            ["dhaka","bangladesh","bangladeshi"],
  "Colombo":          ["colombo","sri lanka","sri lankan"],
  "Kathmandu":        ["kathmandu","nepal","nepalese"],
  "Yangon":           ["yangon","myanmar","burmese","burma"],
  "Hanoi":            ["hanoi","vietnam","vietnamese"],
  "Ho Chi Minh City": ["ho chi minh","saigon","vietnam south"],
  "Ulaanbaatar":      ["ulaanbaatar","mongolia","mongolian"],
  "Tashkent":         ["tashkent","uzbekistan","uzbek"],
  "Almaty":           ["almaty","kazakhstan","kazakh"],
  "Baku":             ["baku","azerbaijan","azerbaijani"],
  "Yerevan":          ["yerevan","armenia","armenian","nagorno"],
  "Tbilisi":          ["tbilisi","georgia country","georgian"],
  // More Oceania
  "Melbourne":        ["melbourne","victoria australia"],
  "Auckland":         ["auckland","new zealand","kiwi","ardern"],
};

// Returns array of all matched city names for an article
function geoTagArticle(article) {
  const text = ((article.headline || "") + " " + (article.summary || "") + " " + (article.region || "")).toLowerCase();
  const matched = new Set();
  for (const [cityName, keywords] of Object.entries(CITY_KEYWORDS)) {
    for (const kw of keywords) {
      if (text.includes(kw.toLowerCase())) { matched.add(cityName); break; }
    }
  }
  // Also try fuzzy match on Claude-assigned region
  if (article.region && article.region !== "National") {
    const regionCity = fuzzyMatchDirect(article.region);
    if (regionCity) matched.add(regionCity.name);
  }
  return [...matched];
}

// fuzzyMatch without calling geoTagArticle (avoids circular ref)
function fuzzyMatchDirect(region) {
  if (!region) return null;
  const r = region.toLowerCase().trim();
  return CITIES.find(c => {
    const n = c.name.toLowerCase();
    if (n === r || r.includes(n) || n.includes(r)) return true;
    if (r.includes("d.c") && c.name === "Washington D.C.") return true;
    if ((r === "washington") && c.name === "Washington D.C.") return true;
    if ((r === "uk" || r === "united kingdom" || r === "england" || r === "britain") && c.name === "London") return true;
    if (r === "france" && c.name === "Paris") return true;
    if (r === "germany" && c.name === "Berlin") return true;
    if (r === "russia" && c.name === "Moscow") return true;
    if (r === "ukraine" && c.name === "Kyiv") return true;
    if ((r === "china" || r === "prc") && c.name === "Beijing") return true;
    if (r === "japan" && c.name === "Tokyo") return true;
    if ((r === "south korea" || r === "korea") && c.name === "Seoul") return true;
    if (r === "taiwan" && c.name === "Taipei") return true;
    if (r === "india" && c.name === "New Delhi") return true;
    if (r === "australia" && c.name === "Canberra") return true;
    if (r === "canada" && c.name === "Ottawa") return true;
    if (r === "israel" && c.name === "Tel Aviv") return true;
    if (r === "iran" && c.name === "Tehran") return true;
    if ((r === "saudi arabia" || r === "saudi") && c.name === "Riyadh") return true;
    if (r === "turkey" && c.name === "Istanbul") return true;
    if (r === "brazil" && c.name === "Brasilia") return true;
    if (r === "pakistan" && c.name === "Islamabad") return true;
    if (r === "afghanistan" && c.name === "Kabul") return true;
    return false;
  });
}

const fuzzyMatch = fuzzyMatchDirect;

const CONTINENT_FILTERS = [
  { id:"all",      label:"All Regions" },
  { id:"americas", label:"Americas" },
  { id:"europe",   label:"Europe" },
  { id:"asia",     label:"Asia" },
  { id:"mideast",  label:"Middle East" },
  { id:"africa",   label:"Africa" },
  { id:"oceania",  label:"Oceania" },
];

const CITY_CONTINENT = {
  "New York":"americas","Los Angeles":"americas","Chicago":"americas","Houston":"americas",
  "Washington D.C.":"americas","San Francisco":"americas","Seattle":"americas","Miami":"americas",
  "Boston":"americas","Atlanta":"americas","Dallas":"americas","Denver":"americas",
  "Las Vegas":"americas","Austin":"americas","Phoenix":"americas","Nashville":"americas",
  "Portland":"americas","Minneapolis":"americas","Detroit":"americas","Philadelphia":"americas",
  "San Diego":"americas","Tampa":"americas","Baltimore":"americas","Salt Lake City":"americas",
  "New Orleans":"americas","Ottawa":"americas","Toronto":"americas","Vancouver":"americas",
  "Mexico City":"americas","Havana":"americas","Caracas":"americas","Bogota":"americas",
  "Brasilia":"americas","Buenos Aires":"americas","Santiago":"americas","Lima":"americas",
  "Quito":"americas","Montevideo":"americas","Asuncion":"americas","La Paz":"americas",
  "London":"europe","Paris":"europe","Berlin":"europe","Moscow":"europe","Kyiv":"europe",
  "Brussels":"europe","Rome":"europe","Madrid":"europe","Amsterdam":"europe","Warsaw":"europe",
  "Stockholm":"europe","Zurich":"europe","Athens":"europe","Vienna":"europe","Lisbon":"europe",
  "Prague":"europe","Budapest":"europe","Bucharest":"europe","Sofia":"europe","Belgrade":"europe",
  "Zagreb":"europe","Vilnius":"europe","Riga":"europe","Tallinn":"europe","Helsinki":"europe",
  "Tel Aviv":"mideast","Gaza":"mideast","Beirut":"mideast","Tehran":"mideast","Riyadh":"mideast",
  "Dubai":"mideast","Istanbul":"mideast","Cairo":"mideast","Baghdad":"mideast","Amman":"mideast",
  "Damascus":"mideast","Doha":"mideast","Kuwait City":"mideast","Muscat":"mideast","Sanaa":"mideast",
  "Nairobi":"africa","Lagos":"africa","Johannesburg":"africa","Accra":"africa","Addis Ababa":"africa",
  "Dakar":"africa","Khartoum":"africa","Kinshasa":"africa","Luanda":"africa","Dar es Salaam":"africa",
  "Beijing":"asia","Shanghai":"asia","Hong Kong":"asia","Tokyo":"asia","Seoul":"asia",
  "Taipei":"asia","Singapore":"asia","Mumbai":"asia","New Delhi":"asia","Islamabad":"asia",
  "Kabul":"asia","Bangkok":"asia","Jakarta":"asia","Kuala Lumpur":"asia","Manila":"asia",
  "Dhaka":"asia","Colombo":"asia","Kathmandu":"asia","Yangon":"asia","Hanoi":"asia",
  "Ho Chi Minh City":"asia","Phnom Penh":"asia","Vientiane":"asia","Ulaanbaatar":"asia",
  "Tashkent":"asia","Almaty":"asia","Baku":"asia","Yerevan":"asia","Tbilisi":"asia",
  "Sydney":"oceania","Canberra":"oceania","Melbourne":"oceania","Auckland":"oceania",
};

function HeatMap({ articles, onRegion }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [selected, setSelected] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hoveredLine, setHoveredLine] = useState(null);
  const [continentFilter, setContinentFilter] = useState("all");

  // Build city counts and connection lines from articles
  const cityCount = {};
  const connections = []; // { from, to, article, color }

  // Filter articles by continent
  const filteredArticles = continentFilter === "all" ? articles : articles.filter(a => {
    const locs = [...geoTagArticle(a), ...(a.locations||[]).map(l=>fuzzyMatch(l)?.name).filter(Boolean)];
    return locs.some(loc => CITY_CONTINENT[loc] === continentFilter);
  });

  filteredArticles.forEach(a => {
    // Get all locations for this article (from keyword scan + Claude locations array)
    const kwLocs = geoTagArticle(a);
    const claudeLocs = (a.locations || []).map(l => fuzzyMatch(l)).filter(Boolean).map(c => c.name);
    const allLocs = [...new Set([...kwLocs, ...claudeLocs])].map(n => CITIES.find(c => c.name === n)).filter(Boolean);

    // Count each city
    allLocs.forEach(city => {
      cityCount[city.name] = (cityCount[city.name] || 0) + 1;
    });

    // Draw a connection line for each pair of cities in this article
    if (allLocs.length >= 2) {
      for (let i = 0; i < allLocs.length - 1; i++) {
        for (let j = i + 1; j < allLocs.length; j++) {
          connections.push({
            from: allLocs[i],
            to: allLocs[j],
            article: a,
            color: leanColor(a.lean),
          });
        }
      }
    }
  });

  // Initialize Mapbox
  useEffect(() => {
    if (mapInstance.current) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://api.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.js";
    script.onload = () => {
      window.mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new window.mapboxgl.Map({
        container: mapRef.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: [15, 25], zoom: 1.5, minZoom: 1, maxZoom: 10,
      });
      map.on("load", () => { setMapLoaded(true); mapInstance.current = map; });
    };
    document.head.appendChild(script);
    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, []);

  // Store connections in a ref so click handlers can access current data
  const connectionsRef = useRef([]);
  connectionsRef.current = connections;

  // Draw connections + markers whenever articles or map changes
  useEffect(() => {
    if (!mapLoaded || !mapInstance.current) return;
    const map = mapInstance.current;

    // Remove ALL old layers and sources cleanly
    [
      "clarion-labels","clarion-dots-outer","clarion-dots",
      "clarion-lines","clarion-lines-bg"
    ].forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id); } catch(e){} });
    [
      "clarion-cities","clarion-connections"
    ].forEach(id => { try { if (map.getSource(id)) map.removeSource(id); } catch(e){} });

    // ── CONNECTION LINES (GeoJSON layer — canvas-anchored, never drifts) ──
    const lineFeatures = connections.map((conn, idx) => ({
      type: "Feature",
      properties: { color: conn.color, idx,
        fromName: conn.from.name, toName: conn.to.name },
      geometry: {
        type: "LineString",
        coordinates: [[conn.from.lng, conn.from.lat], [conn.to.lng, conn.to.lat]],
      },
    }));

    map.addSource("clarion-connections", {
      type: "geojson",
      data: { type: "FeatureCollection", features: lineFeatures },
    });
    map.addLayer({
      id: "clarion-lines-bg", type: "line", source: "clarion-connections",
      paint: { "line-color": ["get","color"], "line-width": 4, "line-opacity": 0.07, "line-blur": 6 },
    });
    map.addLayer({
      id: "clarion-lines", type: "line", source: "clarion-connections",
      paint: { "line-color": ["get","color"], "line-width": 1.4, "line-opacity": 0.5, "line-dasharray": [3,3] },
    });

    // ── CITY DOTS (circle layer — 100% canvas-anchored, zero drift) ──
    const cityFeatures = CITIES
      .filter(city => cityCount[city.name] > 0)
      .map(city => ({
        type: "Feature",
        properties: {
          name: city.name,
          count: cityCount[city.name],
          // radius scales with count
          radius: Math.min(6 + cityCount[city.name] * 3, 22),
        },
        geometry: { type: "Point", coordinates: [city.lng, city.lat] },
      }));

    map.addSource("clarion-cities", {
      type: "geojson",
      data: { type: "FeatureCollection", features: cityFeatures },
    });

    // Outer glow ring
    map.addLayer({
      id: "clarion-dots-outer", type: "circle", source: "clarion-cities",
      paint: {
        "circle-radius": ["interpolate",["linear"],["get","radius"], 6,10, 22,28],
        "circle-color": "#E8956D",
        "circle-opacity": 0.18,
        "circle-stroke-width": 0,
      },
    });

    // Main dot
    map.addLayer({
      id: "clarion-dots", type: "circle", source: "clarion-cities",
      paint: {
        "circle-radius": ["interpolate",["linear"],["get","radius"], 6,6, 22,22],
        "circle-color": "#E8956D",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
        "circle-opacity": 0.95,
      },
    });

    // Count label
    map.addLayer({
      id: "clarion-labels", type: "symbol", source: "clarion-cities",
      layout: {
        "text-field": ["to-string", ["get","count"]],
        "text-size": 10,
        "text-font": ["DIN Offc Pro Bold","Arial Unicode MS Bold"],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: { "text-color": "#ffffff", "text-halo-color": "#E8956D", "text-halo-width": 1 },
    });

    // ── CLICK HANDLERS ──
    map.on("click", "clarion-dots", (e) => { if(navigator.vibrate) navigator.vibrate(8);
      const name = e.features[0]?.properties?.name;
      if (name) {
        setSelected({ type:"city", name });
        onRegion && onRegion(name);
        const feat = e.features[0];
        const coords = feat.geometry.coordinates;
        // Zoom to continent if filter active, else zoom to city
        map.flyTo({ center: coords, zoom: continentFilter !== "all" ? 4 : 5, speed: 1.2 });
      }
    });
    map.on("click", "clarion-lines", (e) => {
      const idx = e.features[0]?.properties?.idx;
      if (idx !== undefined && connectionsRef.current[idx]) {
        setSelected({ type:"line", conn: connectionsRef.current[idx] });
      }
    });

    // Cursor changes
    ["clarion-dots","clarion-dots-outer"].forEach(layer => {
      map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
    });
    map.on("mouseenter", "clarion-lines", () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", "clarion-lines", () => { map.getCanvas().style.cursor = ""; });

  }, [mapLoaded, articles, continentFilter]);

  // Auto-fly to continent when filter changes
  useEffect(() => {
    if (!mapLoaded || !mapInstance.current) return;
    const centers = {
      all:      { center:[15,25],    zoom:1.5 },
      americas: { center:[-80,15],   zoom:2.2 },
      europe:   { center:[15,52],    zoom:3.2 },
      asia:     { center:[105,30],   zoom:2.8 },
      mideast:  { center:[45,27],    zoom:3.5 },
      africa:   { center:[20,5],     zoom:2.8 },
      oceania:  { center:[145,-30],  zoom:3.5 },
    };
    const c = centers[continentFilter] || centers.all;
    mapInstance.current.flyTo({ center:c.center, zoom:c.zoom, speed:1.4, essential:true });
  }, [mapLoaded, continentFilter]);

  const selectedArticles = selected?.type === "city"
    ? articles.filter(a => {
        const locs = [...geoTagArticle(a), ...(a.locations||[]).map(l=>fuzzyMatch(l)?.name).filter(Boolean)];
        return locs.includes(selected.name);
      })
    : [];

  return (
    <div>
      {/* Header */}
      <h2 style={{fontFamily:F.display,fontSize:22,fontWeight:700,color:C.text,margin:"0 0 12px",letterSpacing:"-0.02em"}}>World News Map</h2>

      {/* Continent filter — native dropdown */}
      <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:10}}>
        <div style={{position:"relative", flex:1}}>
          <select
            value={continentFilter}
            onChange={e => setContinentFilter(e.target.value)}
            style={{
              width:"100%", appearance:"none", WebkitAppearance:"none",
              background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:10, padding:"9px 36px 9px 14px",
              fontFamily:F.text, fontSize:13, fontWeight:500,
              color:C.text, cursor:"pointer", outline:"none",
            }}
          >
            {CONTINENT_FILTERS.map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          {/* Chevron icon */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted}
            strokeWidth="2" strokeLinecap="round"
            style={{position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none"}}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </div>
        {continentFilter !== "all" && (
          <button onClick={()=>setContinentFilter("all")} style={{
            ...glassBtn(false), padding:"9px 14px", fontSize:12,
            borderRadius:10, flexShrink:0, color:C.muted,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{display:"inline-block",verticalAlign:"middle",marginRight:4}}><path d="M18 6L6 18M6 6l12 12"/></svg>
            Clear
          </button>
        )}
      </div>

      <div ref={mapRef} style={{width:"100%",height:400,borderRadius:16,overflow:"hidden",marginBottom:0,border:`1px solid ${C.border}`}}/>

      {/* ── MAP LEGEND — Apple News style, always visible below map ── */}
      <div style={{
        background:C.surface, borderRadius:"0 0 16px 16px",
        borderLeft:`1px solid ${C.border}`, borderRight:`1px solid ${C.border}`,
        borderBottom:`1px solid ${C.border}`,
        padding:"14px 16px", marginBottom:16,
      }}>
        <p style={{
          fontFamily:F.text, fontSize:10, fontWeight:700, color:C.muted,
          letterSpacing:"0.09em", textTransform:"uppercase", margin:"0 0 12px",
        }}>Map Key</p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 16px"}}>

          {/* Dot */}
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <div style={{position:"relative", width:20, height:20, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center"}}>
              <div style={{width:20, height:20, borderRadius:"50%", background:C.orange+"22", position:"absolute"}}/>
              <div style={{width:12, height:12, borderRadius:"50%", background:C.orange, border:"2px solid white"}}/>
            </div>
            <div>
              <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.text, margin:0}}>City dot</p>
              <p style={{fontFamily:F.text, fontSize:10, color:C.muted, margin:0, lineHeight:1.4}}>Size = story volume</p>
            </div>
          </div>

          {/* Number */}
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <div style={{width:20, height:20, borderRadius:"50%", background:C.orange, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
              <span style={{fontFamily:F.text, fontSize:10, fontWeight:700, color:"#fff"}}>4</span>
            </div>
            <div>
              <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.text, margin:0}}>Story count</p>
              <p style={{fontFamily:F.text, fontSize:10, color:C.muted, margin:0, lineHeight:1.4}}>Stories in that city</p>
            </div>
          </div>

          {/* Left line */}
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <svg width="28" height="14" style={{flexShrink:0}}>
              <line x1="0" y1="7" x2="28" y2="7" stroke={C.left} strokeWidth="2" strokeDasharray="4,3"/>
            </svg>
            <div>
              <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.left, margin:0}}>Left</p>
              <p style={{fontFamily:F.text, fontSize:10, color:C.muted, margin:0, lineHeight:1.4}}>Left-leaning source</p>
            </div>
          </div>

          {/* Center line */}
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <svg width="28" height="14" style={{flexShrink:0}}>
              <line x1="0" y1="7" x2="28" y2="7" stroke={C.center} strokeWidth="2" strokeDasharray="4,3"/>
            </svg>
            <div>
              <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.center, margin:0}}>Center</p>
              <p style={{fontFamily:F.text, fontSize:10, color:C.muted, margin:0, lineHeight:1.4}}>Center source</p>
            </div>
          </div>

          {/* Right line — spans full width */}
          <div style={{display:"flex", alignItems:"center", gap:8, gridColumn:"1/-1"}}>
            <svg width="28" height="14" style={{flexShrink:0}}>
              <line x1="0" y1="7" x2="28" y2="7" stroke={C.right} strokeWidth="2" strokeDasharray="4,3"/>
            </svg>
            <div>
              <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.right, margin:0}}>Right</p>
              <p style={{fontFamily:F.text, fontSize:10, color:C.muted, margin:0, lineHeight:1.4}}>Right-leaning source · Dashed lines connect cities mentioned in the same story</p>
            </div>
          </div>

        </div>
      </div>
      {!mapLoaded && <div style={{display:"flex",gap:10,alignItems:"center",justifyContent:"center",padding:"20px 0"}}><Spinner/><span style={{fontFamily:F.text,fontSize:13,color:C.muted}}>Loading map…</span></div>}

      {/* City panel */}
      {selected?.type === "city" && selectedArticles.length > 0 && (
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div>
              <p style={{fontFamily:F.display,fontSize:16,fontWeight:700,color:C.text,margin:"0 0 2px"}}>{selected.name}</p>
              <p style={{fontFamily:F.text,fontSize:11,color:C.muted,margin:0}}>{selectedArticles.length} {selectedArticles.length===1?"story":"stories"}</p>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <button onClick={()=>{onRegion&&onRegion(selected.name);}} style={{
                ...glassBtn(false), padding:"6px 12px", fontSize:11, fontWeight:600, borderRadius:99,
                color:C.orange, border:`1px solid ${C.orange}40`, background:C.orange+"10",
              }}>
                View in Feed ↗
              </button>
              <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:C.muted}}>×</button>
            </div>
          </div>
          {selectedArticles.slice(0,5).map((a,i)=>(
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,marginBottom:8,overflow:"hidden",borderLeft:`3px solid ${leanColor(a.lean)}`}}>
              <div style={{padding:"12px 14px",cursor:"pointer"}} onClick={()=>onRegion&&onRegion(selected.name)}>
                <span style={{fontFamily:F.text,fontSize:10,color:C.muted}}>{a.source} · <span style={{color:leanColor(a.lean),fontWeight:600}}>{a.lean}</span></span>
                <p style={{fontFamily:F.text,fontSize:13,fontWeight:600,color:C.text,margin:"4px 0 0",lineHeight:1.4}}>{decodeHTML(a.headline)}</p>
              </div>
              <div style={{padding:"0 14px 12px",display:"flex",gap:6}}>
                {a.url && (
                  <button onClick={()=>window.open(a.url,"_blank","noopener,noreferrer")} style={{...glassBtn(false),padding:"5px 12px",fontSize:11,fontWeight:600}}>Read ↗</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Line click panel */}
      {selected?.type === "line" && selected.conn && (
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderLeft:`3px solid ${selected.conn.color}`,borderRadius:14,padding:"14px 16px",marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <p style={{fontFamily:F.text,fontSize:12,fontWeight:600,color:C.muted,margin:0}}>
              {selected.conn.from.name} ↔ {selected.conn.to.name}
            </p>
            <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",fontSize:16,cursor:"pointer",color:C.muted}}>×</button>
          </div>
          <p style={{fontFamily:F.text,fontSize:14,fontWeight:600,color:C.text,margin:"0 0 6px",lineHeight:1.35}}>{decodeHTML(selected.conn.article.headline)}</p>
          <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0}}>{selected.conn.article.source} · <span style={{color:selected.conn.color,fontWeight:600}}>{selected.conn.article.lean}</span></p>
        </div>
      )}

      {Object.keys(cityCount).length === 0 && (
        <p style={{fontFamily:F.text,fontSize:14,color:C.muted,textAlign:"center",padding:"20px 0"}}>Stories loading — map will populate shortly.</p>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// DNA TREE — Live sourcing via NewsData.io + Claude analysis
// ─────────────────────────────────────────────────────────────────
const NEWSDATA_KEY = "pub_9bd9b65fe1654838ae735506c126e32e";

// Build a flat list of levels for the tree: level 0 = root, level 1 = children, level 2 = grandchildren
function buildLevels(tree) {
  if (!tree) return [];
  const levels = [];
  // BFS
  const queue = [{ id:"r", node:tree.root, parentId:null }];
  const visited = new Set();
  while (queue.length) {
    const batch = [...queue]; queue.length = 0;
    const level = [];
    for (const item of batch) {
      if (visited.has(item.id)) continue;
      visited.add(item.id);
      level.push(item);
      (item.node.children||[]).forEach(cid => {
        if (tree.nodes[cid] && !visited.has(cid))
          queue.push({ id:cid, node:tree.nodes[cid], parentId:item.id });
      });
    }
    if (level.length) levels.push(level);
  }
  return levels;
}

function DNATree({ articles, initialArticle, onClearQuery }) {
  const [inputVal, setInputVal]   = useState("");
  const [loading,  setLoading]    = useState(false);
  const [sources,  setSources]    = useState(null);  // flat list of source objects
  const [storyTitle, setStoryTitle] = useState("");
  const [error,    setError]      = useState(null);
  const [expanded, setExpanded]   = useState(null);

  // When a DNA button is clicked from the feed, auto-trace that article
  useEffect(() => {
    if (initialArticle) {
      const q = initialArticle.headline || "";
      setInputVal(q);
      traceDNA(q, initialArticle);
      if (onClearQuery) onClearQuery();
    }
  }, [initialArticle?.id]); // eslint-disable-line

  const traceDNA = async (rawQuery, seedArticle = null) => {
    if (!rawQuery.trim()) return;
    setLoading(true); setError(null); setSources(null); setExpanded(null);

    try {
      // ── Step 1: Extract strong keywords ──
      const stopwords = new Set(["the","a","an","is","are","was","were","in","on","at","to","for","of","and","or","but","how","why","what","who","with","after","before","says","said","its","their","this","that","from"]);
      const keywords = rawQuery
        .replace(/[^\w\s]/g,"")
        .split(/\s+/)
        .filter(w => w.length > 3 && !stopwords.has(w.toLowerCase()))
        .slice(0, 7)
        .join(" ");

      // ── Step 2: Fetch from NewsData ──
      const q = encodeURIComponent(keywords);
      let raw = [];
      try {
        const res = await fetch(`https://newsdata.io/api/1/news?apikey=${NEWSDATA_KEY}&q=${q}&language=en&size=10`);
        const json = await res.json();
        raw = (json.results || []).filter(a => a.title && a.source_id).slice(0, 8);
      } catch(fetchErr) {
        console.warn("NewsData fetch failed:", fetchErr);
      }

      // ── Step 3: If NewsData returned nothing, use Claude web_search ──
      // Build context from seed article OR search results
      const seedContext = seedArticle
        ? `SEED ARTICLE:\nSource: ${seedArticle.source}\nHeadline: "${seedArticle.headline}"\nLean: ${seedArticle.lean}\nURL: ${seedArticle.url||""}\n\n`
        : "";

      const articleContext = raw.length > 0
        ? `NEWS SOURCES FOUND (${raw.length}):\n` + raw.map((a,i) =>
            `${i+1}. Source: ${a.source_id} | Date: ${a.pubDate||"unknown"} | Headline: "${a.title}" | URL: ${a.link||""} | Summary: ${(a.description||"").slice(0,180)}`
          ).join("\n")
        : `No pre-fetched articles. Use your knowledge of how this story was covered across outlets.`;

      const prompt = `${seedContext}You are a media analyst tracing how the story "${rawQuery}" was covered across outlets.

${articleContext}

Analyze how different sources framed this story. Return ONLY valid JSON:
{
  "storyTitle": "4-6 word descriptive title",
  "sources": [
    {
      "source": "outlet name",
      "published": "YYYY-MM-DD or approximate",
      "lean": "left or center or right",
      "role": "Original Report OR Early Pickup OR Reframe OR Opinion OR Local Angle OR Follow-up",
      "headline": "actual headline they used",
      "angle": "1-2 sentences: specifically how this outlet framed it — what they emphasized, omitted, or spun",
      "spinFlag": false,
      "spinNote": "required if spinFlag=true: what specifically was misleading",
      "url": "url if available or empty string"
    }
  ]
}

Rules:
- Include 4-8 sources minimum. If you don't have article data, use your knowledge of how major outlets (NYT, Fox, BBC, etc.) covered this story
- Sort oldest to newest
- lean must reflect AllSides ratings: NYT/CNN/Guardian=left, Reuters/BBC/AP=center, Fox/Breitbart/NYPost=right
- angle must be SPECIFIC — not "covered the story" but HOW they framed it (headline word choices, who they quoted, what angle they took)
- spinFlag=true only if framing materially distorts facts`;

      const result = await callClaude(prompt, 1500);
      const cleaned = result.replace(/\`\`\`json|\`\`\`/g,"").trim();
      const match = cleaned.match(/\{[\s\S]*\}/);
      if (!match) throw new Error("JSON parse failed — got: " + cleaned.slice(0,100));
      const parsed = JSON.parse(match[0]);

      if (!parsed.sources || parsed.sources.length === 0) throw new Error("No sources returned");

      // Apply AllSides lean to DNA sources too
      const enriched = parsed.sources.map(s => ({
        ...s,
        lean: getSourceLean(s.source) || s.lean || "center",
      }));

      setSources(enriched);
      setStoryTitle(parsed.storyTitle || rawQuery.split(" ").slice(0,5).join(" "));
    } catch(e) {
      console.error("DNA error:", e);
      setError("Could not trace this story. Try rephrasing the search — e.g. use proper nouns like names or places.");
    }
    setLoading(false);
  };

  const spinCount = (sources||[]).filter(s => s.spinFlag).length;

  return (
    <div>
      {/* Header */}
      <h2 style={{fontFamily:F.display,fontSize:22,fontWeight:700,color:C.text,margin:"0 0 4px",letterSpacing:"-0.02em"}}>Story DNA</h2>
      <p style={{fontFamily:F.text,fontSize:13,color:C.muted,margin:"0 0 18px",lineHeight:1.5}}>Trace how a story spreads — who reported it first and how the framing changed.</p>

      {/* Search bar */}
      <div style={{display:"flex",gap:8,marginBottom:sources||loading ? 20 : 14}}>
        <div style={{flex:1,background:C.surface,borderRadius:12,display:"flex",alignItems:"center",padding:"0 14px",border:`1px solid ${C.border}`}}>
          <input
            value={inputVal}
            onChange={e=>setInputVal(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&inputVal.trim()) traceDNA(inputVal); }}
            placeholder="Search a story or topic…"
            style={{flex:1,background:"transparent",border:"none",padding:"12px 0",fontSize:14,color:C.text,outline:"none",fontFamily:F.text}}
          />
          {inputVal && (
            <button onClick={()=>{ setInputVal(""); setSources(null); setError(null); }}
              style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:16,padding:"0 4px"}}>×</button>
          )}
        </div>
        <button
          onClick={()=>inputVal.trim()&&traceDNA(inputVal)}
          disabled={loading||!inputVal.trim()}
          style={{
            background:C.orange,border:"none",borderRadius:12,
            padding:"0 18px",fontSize:13,fontWeight:700,
            color:"#fff",cursor:"pointer",fontFamily:F.text,
            opacity:loading||!inputVal.trim()?0.45:1,
            flexShrink:0,
          }}>
          {loading ? "…" : "Trace"}
        </button>
      </div>

      {/* Quick picks — shown before any search */}
      {!sources && !loading && !error && articles && articles.length > 0 && (
        <div>
          <p style={{fontFamily:F.text,fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",margin:"0 0 10px"}}>From your feed</p>
          {articles.slice(0,5).map((a,i)=>(
            <button key={i}
              onClick={()=>{ const q=a.headline; setInputVal(q); traceDNA(q, a); }}
              style={{
                display:"flex",alignItems:"flex-start",gap:10,width:"100%",textAlign:"left",
                background:C.surface,border:`1px solid ${C.border}`,
                borderRadius:12,padding:"11px 14px",cursor:"pointer",
                marginBottom:8,
              }}>
              <div style={{width:3,alignSelf:"stretch",borderRadius:2,background:leanColor(a.lean),flexShrink:0,minHeight:20}}/>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontFamily:F.text,fontSize:13,color:C.text,margin:0,lineHeight:1.4}}>{a.headline.slice(0,90)}{a.headline.length>90?"…":""}</p>
                <p style={{fontFamily:F.text,fontSize:11,color:C.muted,margin:"3px 0 0"}}>{a.source}</p>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:2}}><path d="M9 18l6-6-6-6"/></svg>
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:16,padding:"50px 0"}}>
          <Spinner/>
          <p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:0}}>Tracing story…</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{background:C.surface,borderRadius:12,padding:"16px 18px",border:`1px solid ${C.border}`}}>
          <p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:0}}>{error}</p>
        </div>
      )}

      {/* Results */}
      {sources && !loading && (
        <>
          {/* Story title + stats */}
          <div style={{marginBottom:20}}>
            <p style={{fontFamily:F.display,fontSize:17,fontWeight:700,color:C.text,margin:"0 0 10px",lineHeight:1.3}}>{storyTitle}</p>
            <div style={{display:"flex",gap:8}}>
              <div style={{background:C.surface,borderRadius:10,padding:"10px 14px",textAlign:"center",flex:1}}>
                <p style={{fontFamily:F.display,fontSize:20,fontWeight:700,color:C.text,margin:0}}>{sources.length}</p>
                <p style={{fontFamily:F.text,fontSize:10,color:C.muted,margin:0,letterSpacing:"0.06em"}}>SOURCES</p>
              </div>
              <div style={{
                background: spinCount > 0 ? C.breaking+"12" : C.surface,
                border: spinCount > 0 ? `1px solid ${C.breaking}30` : "none",
                borderRadius:10,padding:"10px 14px",textAlign:"center",flex:1
              }}>
                <p style={{fontFamily:F.display,fontSize:20,fontWeight:700,color:spinCount>0?C.breaking:C.text,margin:0}}>{spinCount}</p>
                <p style={{fontFamily:F.text,fontSize:10,color:C.muted,margin:0,letterSpacing:"0.06em"}}>SPIN FLAGS</p>
              </div>
              <div style={{background:C.surface,borderRadius:10,padding:"10px 14px",textAlign:"center",flex:1}}>
                <div style={{display:"flex",justifyContent:"center",gap:4,marginBottom:2}}>
                  {["left","center","right"].map(l=>(
                    <div key={l} style={{width:8,height:8,borderRadius:"50%",background:leanColor(l),opacity: sources.some(s=>s.lean===l)?1:0.2}}/>
                  ))}
                </div>
                <p style={{fontFamily:F.text,fontSize:10,color:C.muted,margin:0,letterSpacing:"0.06em"}}>SPECTRUM</p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div style={{position:"relative"}}>
            {/* Vertical line */}
            <div style={{position:"absolute",left:18,top:0,bottom:0,width:2,background:C.divider,borderRadius:1}}/>

            {sources.map((s, i) => {
              const lc = leanColor(s.lean);
              const isFirst = i === 0;
              const isOpen = expanded === i;

              return (
                <div key={i} style={{display:"flex",gap:0,marginBottom:isOpen?4:12,position:"relative"}}>
                  {/* Timeline dot */}
                  <div style={{
                    width:36,height:36,borderRadius:"50%",flexShrink:0,
                    background: isFirst ? lc : C.surface,
                    border:`2px solid ${lc}`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    zIndex:1,position:"relative",
                    boxShadow: isFirst ? `0 0 0 4px ${lc}18` : "none",
                  }}>
                    {isFirst ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="4" fill="#fff"/></svg>
                    ) : s.spinFlag ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.breaking} strokeWidth="2.2" strokeLinecap="round"><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                    ) : (
                      <div style={{width:8,height:8,borderRadius:"50%",background:lc}}/>
                    )}
                  </div>

                  {/* Card */}
                  <div style={{flex:1,marginLeft:12}}>
                    <div
                      onClick={()=>setExpanded(isOpen ? null : i)}
                      style={{
                        background:C.surface,borderRadius:14,padding:"12px 14px",
                        border:`1px solid ${isOpen ? lc+"60" : C.border}`,
                        borderLeft:`3px solid ${lc}`,
                        cursor:"pointer",
                        transition:"border-color 0.15s",
                      }}>
                      {/* Top row */}
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:4}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                            <span style={{fontFamily:F.text,fontSize:12,fontWeight:700,color:C.text}}>{s.source}</span>
                            <span style={{fontFamily:F.text,fontSize:10,color:lc,fontWeight:600,background:lc+"18",borderRadius:6,padding:"1px 6px"}}>{s.lean}</span>
                            <span style={{fontFamily:F.text,fontSize:10,color:C.muted,background:C.card,borderRadius:6,padding:"1px 6px",border:`1px solid ${C.divider}`}}>{s.role}</span>
                            {isFirst && <span style={{fontFamily:F.text,fontSize:10,color:C.accent,fontWeight:700}}>FIRST</span>}
                            {s.spinFlag && (
                              <span style={{fontFamily:F.text,fontSize:10,color:C.breaking,fontWeight:700,background:C.breaking+"12",borderRadius:6,padding:"1px 6px"}}>⚠ Spin</span>
                            )}
                          </div>
                          <p style={{fontFamily:F.text,fontSize:13,fontWeight:600,color:C.text,margin:0,lineHeight:1.35}}>{s.headline}</p>
                        </div>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,transform:isOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s",marginTop:2}}><path d="M9 18l6-6-6-6"/></svg>
                      </div>
                      {s.published && (
                        <p style={{fontFamily:F.text,fontSize:10,color:C.muted,margin:0}}>{s.published}</p>
                      )}
                    </div>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div style={{
                        background:C.card,borderRadius:"0 0 14px 14px",
                        padding:"14px 16px",marginTop:-4,
                        border:`1px solid ${lc+"40"}`,borderTop:"none",
                        borderLeft:`3px solid ${lc}`,
                        animation:"fadeIn 0.15s ease",
                      }}>
                        <p style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:"0 0 12px",lineHeight:1.6}}>{s.angle}</p>
                        {s.spinFlag && s.spinNote && (
                          <div style={{background:C.breaking+"0d",border:`1px solid ${C.breaking}25`,borderRadius:8,padding:"10px 12px",marginBottom:12}}>
                            <p style={{fontFamily:F.text,fontSize:11,fontWeight:700,color:C.breaking,margin:"0 0 3px",letterSpacing:"0.05em"}}>SPIN DETECTED</p>
                            <p style={{fontFamily:F.text,fontSize:12,color:C.breaking,margin:0,lineHeight:1.5}}>{s.spinNote}</p>
                          </div>
                        )}
                        {s.url && (
                          <button
                            onClick={e=>{e.stopPropagation();window.open(s.url,"_blank","noopener,noreferrer");}}
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 12px",fontFamily:F.text,fontSize:12,color:C.muted,cursor:"pointer"}}>
                            Read article ↗
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FACT CHECK SHEET
// ─────────────────────────────────────────────────────────────────
function FactSheet({ article, onClose }) {
  const [res,setRes]=useState(null);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{
    const factPrompt = [
      `You are a rigorous fact-checker. Analyze this specific headline and source carefully.`,
      `Headline: "${article.headline}"`,
      `Source: ${article.source}`,
      `Summary: ${article.summary||"Not provided"}`,
      ``,
      `Based on your knowledge of this source's credibility and the specific claims made:`,
      `Return ONLY this JSON (no markdown, no backticks):`,
      `{"verdict":"Verified OR Likely Accurate OR Needs Context OR Disputed OR Unverified",`,
      `"confidence":<integer 0-100 — do NOT default to 75, be specific>,`,
      `"bias_rating":"left OR center OR right",`,
      `"red_flags":["specific concern about THIS story — not generic advice. Empty array if none"],`,
      `"missing_context":"one sentence about what context THIS story lacks",`,
      `"recommendation":"one specific action for the reader",`,
      `"key_claims":["the 2-3 main factual claims in this headline"]}`,
      ``,
      `Rules: Reuters/AP/BBC high credibility (85-95%). Known partisan outlets lower. red_flags must be story-specific.`
    ].join("\n");
    callClaude(factPrompt)
      .then(t=>{
        try{
          const parsed=JSON.parse(t.replace(/```json|```/g,"").trim());
          parsed.bias_rating=getSourceLean(article.source)||parsed.bias_rating||article.lean;
          setRes(parsed);
        }
        catch{setRes({verdict:"Needs Context",confidence:60,bias_rating:getSourceLean(article.source)||article.lean,red_flags:["Full analysis unavailable"],missing_context:"Verify with multiple sources before sharing.",recommendation:"Cross-reference with Reuters or AP for independent verification.",key_claims:[]});}
        setLoading(false);
      });
  },[]);
  const vc=res?(res.verdict==="Verified"?C.center:res.verdict==="Disputed"?C.right:C.breaking):C.muted;
  return(
    <Sheet onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
        <div>
          <h3 style={{fontFamily:F.display,fontSize:20,fontWeight:700,color:C.text,margin:0,letterSpacing:"-0.02em"}}>Fact Check</h3>
          <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:"2px 0 0"}}>Clarion AI Analysis</p>
        </div>
        <button onClick={onClose} style={{background:C.surface,border:"none",borderRadius:99,width:30,height:30,fontSize:14,cursor:"pointer",color:C.muted}}>×</button>
      </div>
      <p style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:"0 0 20px",lineHeight:1.5,fontStyle:"italic",paddingLeft:12,borderLeft:`2px solid ${C.divider}`}}>"{article.headline}"</p>
      {loading?(
        <div style={{display:"flex",gap:12,alignItems:"center",padding:"28px 0"}}>
          <Spinner/><p style={{fontFamily:F.text,fontSize:13,color:C.muted,margin:0}}>Analyzing…</p>
        </div>
      ):res&&(
        <>
          <div style={{display:"flex",gap:10,marginBottom:14}}>
            {[[res.verdict,vc,"Verdict"],[`${res.confidence}%`,C.text,"Confidence"],[res.bias_rating,leanColor(res.bias_rating),"Lean"]].map(([v,c,l])=>(
              <div key={l} style={{flex:1,background:C.surface,borderRadius:12,padding:"13px 10px",textAlign:"center"}}>
                <div style={{fontFamily:F.text,fontSize:12,fontWeight:600,color:c,textTransform:"capitalize"}}>{v}</div>
                <div style={{fontFamily:F.text,fontSize:10,color:C.muted,marginTop:3}}>{l}</div>
              </div>
            ))}
          </div>
          {res.key_claims?.length>0&&(
            <div style={{background:C.surface,borderRadius:12,padding:"12px 14px",marginBottom:12}}>
              <p style={{fontFamily:F.text,fontSize:10,fontWeight:600,color:C.muted,margin:"0 0 6px",letterSpacing:"0.06em"}}>KEY CLAIMS</p>
              {res.key_claims.map((c,i)=><p key={i} style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:i>0?"4px 0 0":0,lineHeight:1.4}}>· {c}</p>)}
            </div>
          )}
          {res.red_flags?.length>0&&(
            <div style={{background:"#FFF5F5",borderRadius:12,padding:"12px 14px",marginBottom:12}}>
              <p style={{fontFamily:F.text,fontSize:11,fontWeight:600,color:C.right,margin:"0 0 6px"}}>⚠ Red Flags</p>
              {res.red_flags.map((f,i)=><p key={i} style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:i>0?"4px 0 0":0}}>· {f}</p>)}
            </div>
          )}
          <div style={{background:C.surface,borderRadius:12,padding:"12px 14px",marginBottom:12}}>
            <p style={{fontFamily:F.text,fontSize:10,fontWeight:600,color:C.muted,margin:"0 0 4px",letterSpacing:"0.06em"}}>MISSING CONTEXT</p>
            <p style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:0,lineHeight:1.5}}>{res.missing_context}</p>
          </div>
          <p style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:0,lineHeight:1.5}}>{res.recommendation}</p>
        </>
      )}
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────
// JOURNALIST SHEET
// ─────────────────────────────────────────────────────────────────
function JournalistSheet({ source, onClose }) {
  const [aiInsight,setAiInsight]=useState("");
  const [loadingAI,setLoadingAI]=useState(false);
  const j=JOURNALISTS[source];
  const loadInsight=async()=>{
    setLoadingAI(true);
    const t=await callClaude(`In 2 sentences, give a neutral assessment of ${j?.outlet}'s journalistic reputation, editorial approach, and notable strengths or weaknesses. Be factual and balanced.`);
    setAiInsight(t.replace(/```/g,"").trim());
    setLoadingAI(false);
  };
  if(!j) return(
    <Sheet onClose={onClose}>
      <p style={{fontFamily:F.text,fontSize:14,color:C.muted,textAlign:"center",padding:"20px 0"}}>No profile available for this source yet.</p>
    </Sheet>
  );
  const totalLean=j.leanHistory.left+j.leanHistory.center+j.leanHistory.right;
  const trustColor=j.trustScore>=85?"#3A9E6A":j.trustScore>=65?"#5CB87A":"#80C994";
  return(
    <Sheet onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
            <h3 style={{fontFamily:F.display,fontSize:20,fontWeight:700,color:C.text,margin:0,letterSpacing:"-0.02em"}}>{j.outlet}</h3>
            {j.verified&&<span style={{fontSize:11,color:C.center,fontWeight:600}}>✓</span>}
          </div>
          <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0}}>{j.beat}</p>
        </div>
        <button onClick={onClose} style={{background:C.surface,border:"none",borderRadius:99,width:30,height:30,fontSize:14,cursor:"pointer",color:C.muted}}>×</button>
      </div>
      <div style={{display:"flex",gap:12,marginBottom:22}}>
        {[["Trust Score",j.trustScore,trustColor,j.trustScore>=85?"High Trust":j.trustScore>=65?"Moderate Trust":"Low Trust"],["Accuracy",j.accuracyScore,j.accuracyScore>=85?"#3A9E6A":"#5CB87A",j.accuracyScore>=85?"Highly Accurate":"Generally Accurate"]].map(([label,score,color,sublabel])=>(
          <div key={label} style={{flex:1,background:C.surface,borderRadius:14,padding:"16px 10px",textAlign:"center"}}>
            <TrustMeter score={score} size={68}/>
            <p style={{fontFamily:F.text,fontSize:11,fontWeight:600,color,margin:"5px 0 0"}}>{sublabel}</p>
            <p style={{fontFamily:F.text,fontSize:10,color:C.muted,margin:"1px 0 0"}}>{label}</p>
          </div>
        ))}
      </div>
      <div style={{marginBottom:20}}>
        <p style={{fontFamily:F.text,fontSize:10,fontWeight:600,color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",margin:"0 0 8px"}}>Political Lean</p>
        <div style={{display:"flex",height:7,borderRadius:99,overflow:"hidden",marginBottom:7}}>
          <div style={{width:`${(j.leanHistory.left/totalLean)*100}%`,background:C.left}}/>
          <div style={{width:`${(j.leanHistory.center/totalLean)*100}%`,background:C.center}}/>
          <div style={{width:`${(j.leanHistory.right/totalLean)*100}%`,background:C.right}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontFamily:F.text,fontSize:11}}>
          <span style={{color:C.left}}>Left {j.leanHistory.left}%</span>
          <span style={{color:C.center}}>Center {j.leanHistory.center}%</span>
          <span style={{color:C.right}}>Right {j.leanHistory.right}%</span>
        </div>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:18}}>
        {[["Articles/yr",j.articlesThisYear.toLocaleString()],["Corrections",j.corrections],["Retractions",j.retractions]].map(([l,v])=>(
          <div key={l} style={{flex:1,background:C.surface,borderRadius:12,padding:"12px 8px",textAlign:"center"}}>
            <div style={{fontFamily:F.display,fontSize:17,fontWeight:700,color:C.text}}>{v}</div>
            <div style={{fontFamily:F.text,fontSize:10,color:C.muted,marginTop:3}}>{l}</div>
          </div>
        ))}
      </div>
      <p style={{fontFamily:F.text,fontSize:13,color:C.sub,lineHeight:1.65,margin:"0 0 16px"}}>{j.bio}</p>
      <div style={{background:C.surface,borderRadius:12,padding:"13px 14px",marginBottom:16}}>
        <p style={{fontFamily:F.text,fontSize:10,fontWeight:600,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:"0 0 4px"}}>Notable Work</p>
        <p style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:0,lineHeight:1.5}}>{j.notableWork}</p>
      </div>
      {!aiInsight&&!loadingAI&&(
        <button onClick={loadInsight} style={{width:"100%",background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,padding:"11px",fontSize:13,color:C.sub,cursor:"pointer",fontFamily:F.text}}>Get AI Assessment</button>
      )}
      {loadingAI&&<div style={{display:"flex",gap:10,alignItems:"center",padding:"12px 0"}}><Spinner/><p style={{fontFamily:F.text,fontSize:13,color:C.muted,margin:0}}>Analyzing…</p></div>}
      {aiInsight&&(
        <div style={{background:C.surface,borderRadius:12,padding:"13px 14px"}}>
          <p style={{fontFamily:F.text,fontSize:10,fontWeight:600,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:"0 0 6px"}}>AI Assessment</p>
          <p style={{fontFamily:F.text,fontSize:13,color:C.sub,margin:0,lineHeight:1.6}}>{aiInsight}</p>
        </div>
      )}
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────
// PUBLISH SHEET
// ─────────────────────────────────────────────────────────────────
function PublishSheet({ onClose }) {
  const [title,setTitle]=useState("");
  const [body,setBody]=useState("");
  const [done,setDone]=useState(false);
  return(
    <Sheet onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <h3 style={{fontFamily:F.display,fontSize:20,fontWeight:700,color:C.text,margin:0,letterSpacing:"-0.02em"}}>Write</h3>
          <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:"2px 0 0"}}>Publish your perspective to the community</p>
        </div>
        <button onClick={onClose} style={{background:C.surface,border:"none",borderRadius:99,width:30,height:30,fontSize:14,cursor:"pointer",color:C.muted}}>×</button>
      </div>
      {!done?(
        <>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Headline"
            style={{width:"100%",background:C.surface,border:"none",borderRadius:12,padding:"13px 14px",fontSize:16,fontWeight:600,color:C.text,outline:"none",fontFamily:F.display,marginBottom:10,boxSizing:"border-box",letterSpacing:"-0.01em"}}/>
          <textarea value={body} onChange={e=>setBody(e.target.value)} placeholder="Write your perspective…" rows={5}
            style={{width:"100%",background:C.surface,border:"none",borderRadius:12,padding:"13px 14px",fontSize:14,color:C.text,outline:"none",fontFamily:F.text,resize:"none",boxSizing:"border-box",marginBottom:16,lineHeight:1.6}}/>
          <button onClick={()=>{if(title&&body)setDone(true);}}
            style={{width:"100%",background:C.text,border:"none",borderRadius:10,padding:"14px",fontSize:15,color:"#fff",cursor:"pointer",fontFamily:F.text,fontWeight:600}}>
            Publish
          </button>
        </>
      ):(
        <div style={{textAlign:"center",padding:"24px 0"}}>
          <p style={{fontFamily:F.display,fontSize:20,fontWeight:700,color:C.text,margin:"0 0 6px",letterSpacing:"-0.02em"}}>Published.</p>
          <p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:0}}>Your take is live in the community feed.</p>
        </div>
      )}
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// ONBOARDING
// ─────────────────────────────────────────────────────────────────
const ONBOARDING_SLIDES = [
  {
    icon: "◎",
    title: "Welcome to Clarion.",
    color: C.orange,
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M12 3v18M5 8l7-5 7 5M5 16l7 5 7-5"/></svg>,
    title: "Know Your Bias",
    body: "Clarion tracks which perspectives you read and shows your Balance Score over time. See when you're drifting into an echo chamber.",
    color: C.left,
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M7 3c0 4 10 4 10 8S7 15 7 19M17 3c0 4-10 4-10 8s10 4 10 8"/></svg>,
    title: "Trace Every Story",
    body: "Use Story DNA to see who broke a story, how it spread, and where spin was introduced across the political spectrum.",
    color: C.right,
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M5 4h14v17l-7-4-7 4V4z"/></svg>,
    title: "Follow Topics",
    body: "Pick the topics you care about most and Clarion will surface those stories first — while still keeping your feed balanced.",
    color: C.accent,
  },
];

function OnboardingScreen({ onDone }) {
  const [slide, setSlide] = useState(0);
  const s = ONBOARDING_SLIDES[slide];
  const isLast = slide === ONBOARDING_SLIDES.length - 1;
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background: C.bg,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:"40px 32px",
    }}>
      {/* Progress dots */}
      <div style={{display:"flex", gap:6, marginBottom:48}}>
        {ONBOARDING_SLIDES.map((_,i) => (
          <div key={i} onClick={()=>setSlide(i)} style={{
            width: i===slide ? 24 : 6, height:6, borderRadius:3,
            background: i===slide ? s.color : C.border,
            transition:"all 0.3s", cursor:"pointer",
          }}/>
        ))}
      </div>

      {/* Icon */}
      <div style={{
        width:96, height:96, borderRadius:28, marginBottom:32,
        background: s.color+"15", border:`2px solid ${s.color}30`,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:40,
      }}>{s.icon}</div>

      {/* Text */}
      <h1 style={{
        fontFamily:"'Times New Roman', Times, serif",
        fontSize:28, fontWeight:700, color:C.text,
        textAlign:"center", margin:"0 0 16px",
        letterSpacing:"-0.04em", lineHeight:1.2,
      }}>
        {slide === 0 ? (
          <>Welcome to Clar<span style={{fontStyle:"italic"}}>i</span>on.</>
        ) : s.title}
      </h1>
      <p style={{
        fontFamily:F.text, fontSize:16, color:C.sub,
        textAlign:"center", lineHeight:1.7, margin:"0 0 48px",
        maxWidth:300,
      }}>{s.body}</p>

      {/* Buttons */}
      <div style={{display:"flex", gap:12, width:"100%", maxWidth:300}}>
        {!isLast && (
          <button onClick={onDone} style={{
            flex:1, padding:"14px", fontFamily:F.text, fontSize:14,
            color:C.muted, background:"transparent", border:`1px solid ${C.border}`,
            borderRadius:14, cursor:"pointer",
          }}>Skip</button>
        )}
        <button onClick={()=>isLast ? onDone() : setSlide(s=>s+1)} style={{
          flex:2, padding:"14px", fontFamily:F.text, fontSize:15, fontWeight:600,
          color:"#fff", background:s.color, border:"none",
          borderRadius:14, cursor:"pointer",
          boxShadow:`0 4px 16px ${s.color}40`,
        }}>{isLast ? "Start Reading" : "Next"}</button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// COMPARE COVERAGE — side by side left/center/right on one story
// ─────────────────────────────────────────────────────────────────
function CompareView({ story, allArticles, onClose }) {
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const result = await callClaude(
          `A news story: "${story.headline}" (Source: ${story.source}).
Find or imagine how a left-leaning, center, and right-leaning outlet would cover this SAME story differently.
Return ONLY valid JSON:
{
  "topic": "short topic label",
  "left":   { "source": "outlet name", "headline": "how left outlet headlines it", "angle": "1 sentence on their framing", "quote": "key phrase they'd use" },
  "center": { "source": "outlet name", "headline": "neutral headline", "angle": "1 sentence on their framing", "quote": "key phrase" },
  "right":  { "source": "outlet name", "headline": "how right outlet headlines it", "angle": "1 sentence on their framing", "quote": "key phrase they'd use" }
}`
        );
        const clean = result.replace(/\`\`\`json|\`\`\`/g,"").trim();
        const match = clean.match(/\{[\s\S]*\}/);
        if (match) setComparison(JSON.parse(match[0]));
      } catch(e) {}
      setLoading(false);
    };
    run();
  }, [story.headline]);

  const cols = comparison ? [
    { key:"left",   label:"Left",   color:C.left,   data:comparison.left },
    { key:"center", label:"Center", color:C.center, data:comparison.center },
    { key:"right",  label:"Right",  color:C.right,  data:comparison.right },
  ] : [];

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background: C.bg, overflowY:"auto",
      display:"flex", flexDirection:"column",
    }}>
      {/* Header */}
      <div style={{
        position:"sticky", top:0, zIndex:10,
        background: C.bg, borderBottom:`1px solid ${C.border}`,
        padding:"16px 20px", display:"flex", alignItems:"center", gap:12,
      }}>
        <button onClick={onClose} style={{
          background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:99, width:32, height:32,
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", fontSize:16, color:C.muted, flexShrink:0,
        }}>←</button>
        <div>
          <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.muted, margin:0, textTransform:"uppercase", letterSpacing:"0.06em"}}>Compare Coverage</p>
          <p style={{fontFamily:F.display, fontSize:14, fontWeight:700, color:C.text, margin:0, lineHeight:1.3}}>{story.headline.slice(0,60)}…</p>
        </div>
      </div>

      <div style={{padding:"20px 16px", maxWidth:640, margin:"0 auto", width:"100%"}}>
        {loading && (
          <div style={{display:"flex", flexDirection:"column", alignItems:"center", padding:"60px 0", gap:16}}>
            <Spinner/>
            <p style={{fontFamily:F.text, fontSize:14, color:C.muted, margin:0}}>Analysing how each side covers this…</p>
          </div>
        )}

        {comparison && !loading && (
          <>
            <p style={{fontFamily:F.text, fontSize:13, color:C.muted, margin:"0 0 20px", textAlign:"center"}}>
              How <strong style={{color:C.text}}>{comparison.topic}</strong> is framed across the spectrum
            </p>

            {/* Three columns stacked on mobile */}
            <div style={{display:"flex", flexDirection:"column", gap:12}}>
              {cols.map(col => (
                <div key={col.key} style={{
                  background: C.card, borderRadius:16,
                  border:`1px solid ${C.border}`,
                  borderLeft:`4px solid ${col.color}`,
                  padding:"16px",
                }}>
                  <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10}}>
                    <span style={{
                      fontSize:10, fontWeight:700, fontFamily:F.text,
                      color:col.color, background:col.color+"18",
                      borderRadius:20, padding:"3px 10px",
                      textTransform:"uppercase", letterSpacing:"0.05em",
                    }}>{col.label}</span>
                    <span style={{fontFamily:F.text, fontSize:12, color:C.muted}}>{col.data?.source}</span>
                  </div>
                  <p style={{
                    fontFamily:F.display, fontSize:15, fontWeight:700,
                    color:C.text, margin:"0 0 8px", lineHeight:1.35,
                  }}>{col.data?.headline}</p>
                  <p style={{fontFamily:F.text, fontSize:13, color:C.sub, margin:"0 0 10px", lineHeight:1.6}}>
                    {col.data?.angle}
                  </p>
                  {col.data?.quote && (
                    <p style={{
                      fontFamily:F.text, fontSize:12, color:col.color,
                      margin:0, fontStyle:"italic",
                      paddingLeft:10, borderLeft:`2px solid ${col.color}40`,
                    }}>"{col.data.quote}"</p>
                  )}
                </div>
              ))}
            </div>

            {/* Key differences summary */}
            <div style={{marginTop:20, background:C.surface, borderRadius:14, padding:"16px"}}>
              <p style={{fontFamily:F.text, fontSize:11, fontWeight:600, color:C.muted, margin:"0 0 8px", textTransform:"uppercase", letterSpacing:"0.06em"}}>What this shows</p>
              <p style={{fontFamily:F.text, fontSize:13, color:C.sub, margin:0, lineHeight:1.6}}>
                The same story can be framed very differently depending on the outlet's perspective. Clarion shows you all three so you can form your own view.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// AUTH MODAL — Sign up / Sign in
// ─────────────────────────────────────────────────────────────────
function AuthModal({ onClose, onAuth }) {
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const submit = async () => {
    setError(null); setSuccess(null);
    if (!email || !password) { setError("Please fill in all fields."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error: e } = await supabase.auth.signUp({
          email, password,
          options: { data: { display_name: name || email.split("@")[0] } }
        });
        if (e) throw e;
        if (data.user && !data.session) {
          setSuccess("Check your email to confirm your account, then sign in.");
        } else if (data.session) {
          onAuth(data.user);
          onClose();
        }
      } else {
        const { data, error: e } = await supabase.auth.signInWithPassword({ email, password });
        if (e) throw e;
        onAuth(data.user);
        onClose();
      }
    } catch(e) {
      setError(e.message || "Something went wrong.");
    }
    setLoading(false);
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9000,
      background:"rgba(0,0,0,0.55)", backdropFilter:"blur(6px)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
    }} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{
        width:"100%", maxWidth:480,
        background:C.card, borderRadius:"24px 24px 0 0",
        padding:"28px 24px 40px",
        animation:"tab-in 0.25s ease",
      }}>
        {/* Handle bar */}
        <div style={{width:36,height:4,borderRadius:2,background:C.divider,margin:"0 auto 24px"}}/>

        {/* Logo */}
        <div style={{textAlign:"center", marginBottom:24}}>
          <div style={{fontFamily:"'Times New Roman',Times,serif", fontSize:32, fontWeight:700,
            color:C.text, letterSpacing:"-0.07em"}}>
            Clar<span style={{fontStyle:"italic"}}>i</span>on.
          </div>
          <p style={{fontFamily:F.text, fontSize:13, color:C.muted, margin:"4px 0 0"}}>
            {mode==="signup" ? "Create your account" : "Welcome back"}
          </p>
        </div>

        {/* Mode toggle */}
        <div style={{display:"flex", background:C.surface, borderRadius:12, padding:3, marginBottom:20}}>
          {["signin","signup"].map(m => (
            <button key={m} onClick={()=>{ setMode(m); setError(null); setSuccess(null); }} style={{
              flex:1, padding:"9px 0", borderRadius:10, border:"none", cursor:"pointer",
              fontFamily:F.text, fontSize:13, fontWeight: mode===m ? 600 : 400,
              background: mode===m ? C.card : "transparent",
              color: mode===m ? C.text : C.muted,
              boxShadow: mode===m ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              transition:"all 0.18s",
            }}>
              {m==="signin" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        {/* Fields */}
        {mode==="signup" && (
          <div style={{marginBottom:12}}>
            <input value={name} onChange={e=>setName(e.target.value)}
              placeholder="Your name (optional)"
              style={{width:"100%", padding:"13px 16px", borderRadius:12, border:`1px solid ${C.border}`,
                background:C.surface, color:C.text, fontFamily:F.text, fontSize:14,
                outline:"none", boxSizing:"border-box"}}/>
          </div>
        )}
        <div style={{marginBottom:12}}>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
            placeholder="Email address"
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{width:"100%", padding:"13px 16px", borderRadius:12, border:`1px solid ${C.border}`,
              background:C.surface, color:C.text, fontFamily:F.text, fontSize:14,
              outline:"none", boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:20}}>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
            placeholder="Password"
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{width:"100%", padding:"13px 16px", borderRadius:12, border:`1px solid ${C.border}`,
              background:C.surface, color:C.text, fontFamily:F.text, fontSize:14,
              outline:"none", boxSizing:"border-box"}}/>
        </div>

        {error && <p style={{fontFamily:F.text, fontSize:13, color:C.right, margin:"0 0 14px", textAlign:"center"}}>{error}</p>}
        {success && <p style={{fontFamily:F.text, fontSize:13, color:"#5CB87A", margin:"0 0 14px", textAlign:"center", lineHeight:1.5}}>{success}</p>}

        <button onClick={submit} disabled={loading} style={{
          width:"100%", padding:"14px", borderRadius:12, border:"none",
          background: loading ? C.muted : C.orange,
          color:"#fff", fontFamily:F.text, fontSize:15, fontWeight:700,
          cursor: loading ? "default" : "pointer",
          transition:"all 0.2s",
          boxShadow:"0 3px 12px rgba(232,149,109,0.35)",
        }}>
          {loading ? "Please wait…" : mode==="signin" ? "Sign In" : "Create Account"}
        </button>

        <button onClick={onClose} style={{
          width:"100%", padding:"12px", marginTop:10, borderRadius:12, border:"none",
          background:"transparent", color:C.muted, fontFamily:F.text, fontSize:14, cursor:"pointer",
        }}>Maybe later</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ERROR BOUNDARY — catches Claude/fetch failures gracefully
// ─────────────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Clarion error:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight:"100vh", display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center",
          background:"#FFFFFF", padding:32, textAlign:"center",
          fontFamily:"-apple-system, sans-serif",
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9A9689" strokeWidth="1.4" strokeLinecap="round" style={{display:"block",margin:"0 auto 16px",opacity:0.5}}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18M3 14h6"/></svg>
          <p style={{fontSize:22, fontWeight:700, color:"#1A1A18", margin:"0 0 8px"}}>Something went wrong</p>
          <p style={{fontSize:14, color:"#9A9689", margin:"0 0 24px", lineHeight:1.6, maxWidth:300}}>
            Clarion hit an unexpected error. Your saved preferences are intact.
          </p>
          <button onClick={()=>{ this.setState({hasError:false,error:null}); window.location.reload(); }}
            style={{background:"#E8956D", border:"none", borderRadius:980, padding:"10px 24px",
              fontSize:14, fontWeight:600, color:"#fff", cursor:"pointer"}}>
            Reload App
          </button>
          {this.state.error && (
            <p style={{fontSize:11, color:"#C0BDB8", marginTop:16, fontFamily:"monospace"}}>
              {this.state.error.message}
            </p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// ── SKELETON LOADER ──
function SkeletonCard({ isLead=false }) {
  const shimmer = {
    background: `linear-gradient(90deg, ${C.surface} 25%, ${C.divider} 50%, ${C.surface} 75%)`,
    backgroundSize:"800px 100%",
    animation:"shimmer 1.4s ease-in-out infinite",
    borderRadius:8,
  };
  return (
    <div style={{borderRadius:18, overflow:"hidden", background:C.card, border:`1px solid ${C.border}`}}>
      <div style={{...shimmer, height: isLead ? 220 : 130, borderRadius:0}}/>
      <div style={{padding:"12px 14px 14px"}}>
        <div style={{...shimmer, height:10, width:"40%", marginBottom:10}}/>
        <div style={{...shimmer, height:14, width:"95%", marginBottom:7}}/>
        <div style={{...shimmer, height:14, width:"80%", marginBottom: isLead?7:0}}/>
        {isLead && <div style={{...shimmer, height:14, width:"60%"}}/>}
      </div>
    </div>
  );
}

function SkeletonFeed() {
  return (
    <div style={{padding:"0 0 20px"}}>
      {/* Carousel skeleton */}
      <div style={{margin:"4px -16px 20px", padding:"0 16px"}}>
        <SkeletonCard isLead={true}/>
      </div>
      {/* Grid skeleton */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10}}>
        <SkeletonCard/><SkeletonCard/>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
        <SkeletonCard/><SkeletonCard/>
      </div>
    </div>
  );
}

// Scroll-tracking dots for the carousel
function CarouselDots({ items, carouselId }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const el = document.getElementById(carouselId);
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / (el.scrollWidth / items.length));
      setActive(Math.min(idx, items.length - 1));
    };
    el.addEventListener("scroll", onScroll, { passive:true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [carouselId, items.length]);
  return (
    <div style={{ display:"flex", justifyContent:"center", gap:5, paddingTop:2 }}>
      {items.map((_, i) => (
        <div key={i} style={{
          width: i === active ? 18 : 6,
          height:6, borderRadius:3,
          background: i === active ? C.orange : C.border,
          transition:"all 0.3s",
        }}/>
      ))}
    </div>
  );
}

// Wrapped export with error boundary
function ClarionFinal() {
  const [tab,setTab]=useState("feed");
  const [category,setCategory]=useState("All");
  const [searchInput,setSearchInput]=useState("");
  const [search,setSearch]=useState("");
  const [bookmarks,setBookmarks]=useState([]);
  const [history,setHistory]=useState([]);
  const [verifying,setVerifying]=useState(null);
  const [showWrite,setShowWrite]=useState(false);
  const [journalist,setJournalist]=useState(null);
  const [regionFilter,setRegionFilter]=useState(null);
  const [notifPermission,setNotifPermission]=useState(typeof Notification!=="undefined"?Notification.permission:"default");
  const [notifEnabled,setNotifEnabled]=useState(()=>{try{return localStorage.getItem("clarion_notif")==="1";}catch{return false;}});
  const [notifTime,setNotifTime]=useState(()=>{try{return localStorage.getItem("clarion_notif_time")||"08:00";}catch{return "08:00";}});
  const [aiArticles,setAiArticles]=useState([]);
  const [aiLoading,setAiLoading]=useState(false);
  const [briefing,setBriefing]=useState(null);
  const [briefingLoading,setBriefingLoading]=useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return !localStorage.getItem("clarion_onboarded"); }
    catch(e) { return true; }
  });
  const [followedTopics, setFollowedTopics] = useState(["Politics","Tech","World"]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [compareStory, setCompareStory] = useState(null);
  const [dnaQuery, setDnaQuery] = useState(null); // article object passed from feed DNA button
  const [expandedCard, setExpandedCard] = useState(null); // id of expanded inline card
  const [prevTab, setPrevTab] = useState(null);
  const [tabTransition, setTabTransition] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [userPrefsLoaded, setUserPrefsLoaded] = useState(false);

  const switchTab = (newTab) => {
    if (newTab === tab) return;
    // Haptic feedback
    if (navigator.vibrate) navigator.vibrate(8);
    setPrevTab(tab);
    setTabTransition(true);
    setTimeout(() => { setTab(newTab); setTabTransition(false); }, 120);
  };

  // Wire C to darkMode — mutate in place so all components see the update
  const theme = darkMode ? DARK : LIGHT;
  Object.assign(C, theme);

  const all=[...aiArticles,...ARTICLES];

  // Sort: followed topics float to top, then rest
  const sortedAll = followedTopics.length > 0
    ? [...all.filter(a => followedTopics.includes(a.category)), ...all.filter(a => !followedTopics.includes(a.category))]
    : all;

  const feed=sortedAll.filter(a=>{
    if(category!=="All"&&a.category!==category) return false;
    if(search&&!a.headline.toLowerCase().includes(search.toLowerCase())&&!a.source.toLowerCase().includes(search.toLowerCase())) return false;
    if(regionFilter){
      // Match against all article locations (keyword-detected + Claude locations)
      const artLocs = [...(geoTagArticle(a)||[]), ...(a.locations||[]).map(l=>fuzzyMatch(l)?.name).filter(Boolean)];
      // Also match Claude region field via fuzzyMatch
      const regionCity = fuzzyMatch(a.region);
      if(regionCity) artLocs.push(regionCity.name);
      if(!artLocs.includes(regionFilter)) return false;
    }
    return true;
  });

  const onRead=(id, article=null)=>{
    haptic(6);
    setHistory(v=>v.includes(id)?v:[...v,id]);
    if (article) syncRead(article);
  };

  // ── HAPTIC HELPER ──
  const haptic = (pattern = 8) => { if (navigator.vibrate) navigator.vibrate(pattern); };

  // ── AUTH: listen for session changes ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setUser(session.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── LOAD user prefs + bookmarks from Supabase when user logs in ──
  useEffect(() => {
    if (!user) return;
    const loadUserData = async () => {
      // Preferences
      const { data: prefs } = await supabase
        .from("user_preferences").select("*").eq("user_id", user.id).single();
      // If user has ever completed onboarding, never show it again
      if (prefs?.onboarded) {
        setShowOnboarding(false);
        try { localStorage.setItem("clarion_onboarded","1"); } catch(e){}
      }
      if (prefs) {
        setDarkMode(prefs.dark_mode ?? false);
        setFollowedTopics(prefs.followed_topics ?? ["Politics","Tech","World"]);
      } else {
        // Create default prefs row
        await supabase.from("user_preferences").insert({
          user_id: user.id, dark_mode: false,
          followed_topics: ["Politics","Tech","World"],
        });
      }
      // Bookmarks
      const { data: bms } = await supabase
        .from("bookmarks").select("article_id, article_data").eq("user_id", user.id);
      if (bms?.length) {
        setBookmarks(bms.map(b => b.article_id));
        // Merge bookmark article data into aiArticles if not already present
        const bmArticles = bms.map(b => b.article_data).filter(Boolean);
        setAiArticles(prev => {
          const ids = new Set(prev.map(a => String(a.id)));
          const newOnes = bmArticles.filter(a => !ids.has(String(a.id)));
          return [...prev, ...newOnes];
        });
      }
      // Reading history
      const { data: hist } = await supabase
        .from("reading_history").select("article_id").eq("user_id", user.id);
      if (hist?.length) setHistory(hist.map(h => h.article_id));
      setUserPrefsLoaded(true);
    };
    loadUserData();
  }, [user]);

  // ── SYNC dark mode to Supabase ──
  useEffect(() => {
    if (!user || !userPrefsLoaded) return;
    supabase.from("user_preferences")
      .update({ dark_mode: darkMode, updated_at: new Date().toISOString() })
      .eq("user_id", user.id);
  }, [darkMode, user, userPrefsLoaded]);

  // ── SYNC followed topics to Supabase ──
  useEffect(() => {
    if (!user || !userPrefsLoaded) return;
    supabase.from("user_preferences")
      .update({ followed_topics: followedTopics, updated_at: new Date().toISOString() })
      .eq("user_id", user.id);
  }, [followedTopics, user, userPrefsLoaded]);

  // ── BOOKMARK: save/remove article to Supabase ──
  const syncBookmark = async (article, isAdding) => {
    if (!user) return;
    const articleId = String(article.id);
    if (isAdding) {
      await supabase.from("bookmarks").upsert({
        user_id: user.id, article_id: articleId, article_data: article,
      });
    } else {
      await supabase.from("bookmarks").delete()
        .eq("user_id", user.id).eq("article_id", articleId);
    }
  };

  // ── READING HISTORY: record article read ──
  const syncRead = async (article) => {
    if (!user) return;
    await supabase.from("reading_history").upsert({
      user_id: user.id,
      article_id: String(article.id),
      lean: article.lean,
      category: article.category,
    });
  };

  // ── SIGN OUT ──
  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setUserPrefsLoaded(false);
    setBookmarks([]);
    setHistory([]);
  };

  // ── REGISTER SERVICE WORKER ──
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js")
        .then(reg => console.log("[Clarion] SW registered:", reg.scope))
        .catch(err => console.warn("[Clarion] SW failed:", err));
    }
    // Re-schedule briefing if already enabled
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      const savedTime = localStorage.getItem("clarion_notif_time") || "08:00";
      if (localStorage.getItem("clarion_notif") === "1") {
        // scheduleLocalBriefing called after component mounts via effect below
        setTimeout(() => {
          const [h,m] = savedTime.split(":").map(Number);
          const now = new Date();
          const next = new Date();
          next.setHours(h, m, 0, 0);
          if (next <= now) next.setDate(next.getDate() + 1);
          const msUntil = next - now;
          const tid = setTimeout(() => {
            if (Notification.permission === "granted") {
              new Notification("Your Clarion Briefing", {
                body: "Good morning. Your personalised news briefing is ready.",
                icon: "/icon-192.png",
                badge: "/icon-192.png",
                tag: "clarion-briefing",
              });
            }
          }, msUntil);
          localStorage.setItem("clarion_notif_tid", String(tid));
        }, 1000);
      }
    }
    // Handle deep links from PWA shortcuts e.g. /?tab=map
    const params = new URLSearchParams(window.location.search);
    const deepTab = params.get("tab");
    if (deepTab && ["feed","map","balance","dna","profile","about"].includes(deepTab)) {
      setTab(deepTab);
    }
  }, []);

  // ── NOTIFICATION HELPERS ──
  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === "granted") {
      setNotifEnabled(true);
      localStorage.setItem("clarion_notif","1");
      scheduleLocalBriefing(notifTime);
    }
  };

  const scheduleLocalBriefing = (time) => {
    // Store the time — the SW or a setTimeout will fire it
    localStorage.setItem("clarion_notif_time", time);
    localStorage.setItem("clarion_notif","1");
    // Cancel any existing alarm timeout
    const existingId = localStorage.getItem("clarion_notif_tid");
    if (existingId) clearTimeout(parseInt(existingId));
    // Calculate ms until next occurrence of chosen time
    const [h,m] = time.split(":").map(Number);
    const now = new Date();
    const next = new Date();
    next.setHours(h, m, 0, 0);
    if (next <= now) next.setDate(next.getDate() + 1);
    const msUntil = next - now;
    const tid = setTimeout(() => {
      if (Notification.permission === "granted") {
        new Notification("Your Clarion Briefing", {
          body: "Good morning. Your personalised news briefing is ready.",
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag: "clarion-briefing",
        });
        // Reschedule for tomorrow
        scheduleLocalBriefing(time);
      }
    }, msUntil);
    localStorage.setItem("clarion_notif_tid", String(tid));
    console.log("[Clarion] Briefing scheduled in", Math.round(msUntil/60000), "mins");
  };

  const disableNotifications = () => {
    setNotifEnabled(false);
    localStorage.setItem("clarion_notif","0");
    const tid = localStorage.getItem("clarion_notif_tid");
    if (tid) { clearTimeout(parseInt(tid)); localStorage.removeItem("clarion_notif_tid"); }
  };

  const CACHE_KEY = "clarion_feed_v5"; // v5: AllSides lean + 100 articles
  const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

  const loadAI = async (forceRefresh = false) => {
    // ── CACHE CHECK — serve instantly if fresh ──
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { ts, articles: ca } = JSON.parse(cached);
          if (Date.now() - ts < CACHE_TTL && ca?.length > 0) {
            setAiArticles(ca);
            setLastUpdated(new Date(ts));
            setAiLoading(false);
            return;
          }
        }
      } catch(e) {}
    }

    setAiLoading(true);

    // ── FETCH RSS/GNEWS ──
    let articles = [];
    try {
      const res = await fetch("https://clarion-proxy.vercel.app/api/gnews");
      const json = await res.json();
      articles = (json.articles || []).filter(a => a.title && a.url).slice(0, 100);
    } catch(e) {
      console.error("Feed fetch failed:", e);
      setAiLoading(false);
      return;
    }

    if (articles.length === 0) { setAiLoading(false); return; }

    // ── SHOW ARTICLES IMMEDIATELY (unenriched) so feed appears instantly ──
    // Keyword-based category pre-assignment so articles show in sections even before enrichment
    const guessCategory = (title="", src="") => {
      const t = (title + " " + src).toLowerCase();
      if (/trump|biden|congress|senate|election|democrat|republican|white house|parliament|minister|president|vote|policy|supreme court/.test(t)) return "Politics";
      if (/war|ukraine|russia|israel|gaza|nato|china|iran|military|missile|troops|attack|conflict|diplomacy/.test(t)) return "World";
      if (/apple|google|microsoft|ai |openai|meta|tech|software|iphone|android|cybersecurity|startup|silicon/.test(t)) return "Tech";
      if (/stock|market|economy|fed|inflation|gdp|earnings|bank|trade|recession|crypto|bitcoin|finance|dollar/.test(t)) return "Business";
      if (/climate|nasa|science|study|research|space|discovery|ocean|species|physics|genome|earthquake/.test(t)) return "Science";
      if (/health|cancer|covid|vaccine|hospital|drug|fda|mental|obesity|disease|medical|surgery/.test(t)) return "Health";
      if (/football|soccer|nfl|nba|mlb|tennis|olympics|sport|championship|league|athlete|world cup/.test(t)) return "Sports";
      if (/music|album|concert|taylor|beyonce|artist|band|singer|grammy|billboard|spotify|pop|rapper/.test(t)) return "Music";
      if (/rescue|charity|award|hero|community|inspir|hope|kind|volunteer|celebration|win|joy/.test(t)) return "Uplifting";
      return "World"; // sensible fallback — not Breaking
    };

    const initial = articles.map((a, i) => {
      const srcName = a.source?.name || "Unknown";
      return {
        id: 200 + i,
        headline: a.title,
        summary: a.description || "",
        source: srcName,
        url: a.url,
        image: a.image || null,
        publishedAt: a.publishedAt || null,
        lean: getSourceLean(srcName) || "center",
        category: guessCategory(a.title, srcName),
        time: "Live", breaking: false, region: "National", verified: true, locations: [],
      };
    });
    setAiArticles(initial);
    setAiLoading(false); // hide spinner — feed visible, enrichment runs quietly

    // ── ENRICH IN BATCHES OF 15 — first batch appears in ~2s ──
    const BATCH = 25; // larger batches = fewer API calls for 100 articles
    const enriched = [...initial];

    for (let b = 0; b < articles.length; b += BATCH) {
      const slice = articles.slice(b, b + BATCH);
      const lineList = slice.map((a, i) =>
        (b+i+1) + `. Source: "${a.source?.name||"Unknown"}" | Headline: "${a.title}"`
      ).join("\n");
      // Use AllSides lookup first, fall back to Claude for unknowns
      const prompt = `Analyze these ${slice.length} news headlines. Return ONLY a JSON array with ${slice.length} objects. Each object: {"lean":"left OR center OR right","category":"Politics OR Tech OR Business OR Science OR World OR Health OR Sports OR Music OR Uplifting OR Breaking","region":"primary city or country","breaking":false,"locations":["up to 3 city or country names — empty array if domestic only"]}. For lean: use established media bias research (AllSides, Ad Fontes) — NYT/Guardian/CNN/NPR=left, Reuters/BBC/AP/ABC/CBS/NBC=center, Fox/Breitbart/Daily Wire/NY Post=right. Be accurate, not all center.\n\n${lineList}`;

      try {
        const raw = await callClaude(prompt);
        const clean = raw.replace(/\`\`\`json|\`\`\`/g,"").trim();
        const match = clean.match(/\[[\s\S]*\]/);
        if (match) {
          const tags = JSON.parse(match[0]);
          tags.forEach((tag, i) => {
            if (enriched[b+i]) {
              // AllSides lookup overrides Claude for known sources
              const knownLean = getSourceLean(enriched[b+i].source);
              enriched[b+i] = { ...enriched[b+i],
                lean: knownLean || tag?.lean || "center",
                category: tag?.category || "Breaking",
                region: tag?.region || "National",
                breaking: tag?.breaking || false,
                locations: tag?.locations || [],
              };
            }
          });
          setAiArticles([...enriched]); // progressive update after each batch
        }
      } catch(e) {
        console.warn("Enrichment batch", b, "failed — articles showing with defaults:", e);
        // Feed already visible — this batch just keeps default lean/category
      }
    }

    // ── CACHE FINAL RESULT ──
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), articles: enriched }));
    } catch(e) {}

    setLastUpdated(new Date());
  };

  // Load Google Fonts (Space Grotesk display, Inter body, Space Mono data)
  useEffect(()=>{
    if (document.getElementById("clarion-fonts")) return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect"; pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect"; pre2.href = "https://fonts.gstatic.com"; pre2.crossOrigin = "anonymous";
    const link = document.createElement("link");
    link.id = "clarion-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&family=IBM+Plex+Mono:wght@400;500;600&family=Newsreader:opsz,wght@6..72,500;6..72,600;6..72,700&display=swap";
    document.head.appendChild(pre1); document.head.appendChild(pre2); document.head.appendChild(link);
  },[]);

  useEffect(()=>{ loadAI(); },[]);

  // Human-readable "last updated" string
  const lastUpdatedLabel = lastUpdated ? (() => {
    const mins = Math.floor((Date.now() - lastUpdated.getTime()) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins/60)}h ago`;
  })() : null;

  // Refresh countdown (re-render every 30s to keep label fresh)
  useEffect(()=>{
    const t = setInterval(()=>setLastUpdated(v=>v?new Date(v):v), 30000);
    return ()=>clearInterval(t);
  },[]);

  // Live search — fetches from NewsData.io by query
  const NEWSDATA_KEY = "pub_9bd9b65fe1654838ae735506c126e32e";
  const doSearch = async (query) => {
    if (!query.trim()) { setSearchResults(null); return; }
    setSearchLoading(true);
    try {
      const q = encodeURIComponent(query.trim().split(" ").slice(0,5).join(" "));
      const res = await fetch(`https://newsdata.io/api/1/news?apikey=${NEWSDATA_KEY}&q=${q}&language=en&size=10`);
      const json = await res.json();
      const results = (json.results || []).filter(a => a.title && a.link).map((a, i) => ({
        id: 900 + i,
        headline: a.title,
        summary: a.description || "",
        source: a.source_id || "Unknown",
        url: a.link,
        image: a.image_url || null,
        publishedAt: a.pubDate || null,
        lean: getSourceLean(a.source_id) || "center", category: "Breaking", time: "Live",
        breaking: false, region: "National", verified: true,
      }));
      setSearchResults(results);
    } catch(e) { setSearchResults([]); }
    setSearchLoading(false);
  };

  // Detect breaking stories — any article flagged breaking that hasn't been dismissed

  const loadBriefing=async()=>{
    setBriefingLoading(true);
    try{
      const t=await callClaude("Daily news briefing. Return JSON: overview (2 sentences), stories ([{headline,insight}]×3), uplifting (1 warm closing sentence).");
      setBriefing(JSON.parse(t.replace(/```json|```/g,"").trim()));
    }catch(e){}
    setBriefingLoading(false);
  };

  return(
    <div style={{
      background: C.bg,
      minHeight: "100vh",
      fontFamily: F.text,
    }}>
      <style>{`
        @keyframes clarion-spin { to { transform: rotate(360deg); } }
        @keyframes tab-pop { 0% { transform: scale(0.85); opacity:0.5; } 100% { transform: scale(1); opacity:1; } }
        @keyframes tab-in  { 0% { opacity:0; transform:translateY(10px); } 100% { opacity:1; transform:translateY(0); } }
        @keyframes tab-out { 0% { opacity:1; transform:translateY(0); }   100% { opacity:0; transform:translateY(-8px); } }
        @keyframes shimmer { 0% { background-position:-400px 0; } 100% { background-position:400px 0; } }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.4} }
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { margin: 0; padding: 0; background: ${C.bg}; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {showOnboarding && <OnboardingScreen onDone={()=>{
        setShowOnboarding(false);
        try { localStorage.setItem("clarion_onboarded","1"); } catch(e){}
        if(user) supabase.from("user_preferences")
          .update({ onboarded: true, updated_at: new Date().toISOString() })
          .eq("user_id", user.id);
      }}/>}
      {showAuth && <AuthModal onClose={()=>setShowAuth(false)} onAuth={u=>setUser(u)}/>}
      {compareStory && <CompareView story={compareStory} allArticles={all} onClose={()=>setCompareStory(null)}/>}

      {verifying  && <FactSheet article={verifying} onClose={()=>setVerifying(null)}/>}
      {showWrite  && <PublishSheet onClose={()=>setShowWrite(false)}/>}
      {journalist && <JournalistSheet source={journalist} onClose={()=>setJournalist(null)}/>}

      {/* ── HEADER ── */}
      <div style={{
        position:"sticky", top:0, zIndex:100,
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{maxWidth:640, margin:"0 auto", padding:"16px 20px 12px"}}>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6}}>
            {/* Masthead nameplate */}
            <ClarionLogoFull height={32}/>
            {/* Right icons — search + circular refresh */}
            <div style={{display:"flex", gap:8, alignItems:"center"}}>
              <button onClick={()=>setShowSearch(v=>!v)} style={{
                width:36,height:36,borderRadius:980,
                display:"flex",alignItems:"center",justifyContent:"center",
                background:C.surface, border:`1px solid ${C.border}`,
                cursor:"pointer", flexShrink:0, transition:"all 0.2s",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={showSearch?C.text:C.muted} strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
                </svg>
              </button>
              <button onClick={()=>{ if(navigator.vibrate) navigator.vibrate(10); loadAI(true); }} disabled={aiLoading} style={{
                width:36,height:36,borderRadius:980,
                display:"flex",alignItems:"center",justifyContent:"center",
                background:C.text, border:"none", cursor:"pointer",
                opacity:aiLoading?0.5:1, transition:"all 0.2s", flexShrink:0,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.bg} strokeWidth="2.5" strokeLinecap="round">
                  <path d="M1 4v6h6M23 20v-6h-6"/><path d="M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Last updated indicator — restored */}
          {lastUpdatedLabel && (
            <div style={{display:"flex", alignItems:"center", gap:5, marginBottom:8}}>
              <div style={{width:6,height:6,borderRadius:"50%",
                background: aiLoading ? C.muted : "#5CB87A", flexShrink:0,
                animation: aiLoading ? "none" : "pulse-dot 2s ease-in-out infinite"}}/>
              <span style={{fontFamily:F.mono, fontSize:10, color:C.muted, letterSpacing:"0.06em", textTransform:"uppercase"}}>
                {aiLoading ? "Refreshing…" : `Updated ${lastUpdatedLabel}`}
              </span>
            </div>
          )}

          {/* Search — icon that expands */}
          {showSearch && (
            <div style={{
              background: C.card, border:`1px solid ${C.border}`,
              borderRadius: 16,
              display:"flex", alignItems:"center", gap:8,
              padding:"0 14px",
              marginBottom: 2,
              animation: "tab-pop 0.15s ease",
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
              </svg>
              <input
                autoFocus
                value={searchInput}
                onChange={e=>setSearchInput(e.target.value)}
                onKeyDown={e=>{
                  if(e.key==="Enter"){ doSearch(searchInput); setShowSearch(false); setTab("feed"); }
                  if(e.key==="Escape"){ setSearch(""); setSearchInput(""); setSearchResults(null); setShowSearch(false); }
                }}
                placeholder="Search stories, topics, sources…"
                style={{
                  flex:1, background:"transparent", border:"none",
                  padding:"12px 0", fontSize:14, color:C.text,
                  outline:"none", fontFamily:F.text,
                }}
              />
              <button onClick={()=>{ setSearch(""); setSearchInput(""); setShowSearch(false); }}
                style={{background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:15, lineHeight:1}}>×</button>
            </div>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{
        maxWidth:640, margin:"0 auto", padding:"8px 16px 120px",
        animation: tabTransition ? "tab-out 0.12s ease forwards" : "tab-in 0.18s ease forwards",
      }}>

        {/* FEED */}
        {tab==="feed" && (
          <>
            {/* Category pills — polished scrollable row */}
            <div style={{ position:"relative", margin:"12px -16px 8px" }}>
              {/* Soft fade on both edges */}
              <div style={{ position:"absolute", left:0, top:0, bottom:0, width:24, zIndex:2, background:`linear-gradient(to right, ${C.bg} 40%, transparent)`, pointerEvents:"none" }}/>
              <div style={{ position:"absolute", right:0, top:0, bottom:0, width:24, zIndex:2, background:`linear-gradient(to left, ${C.bg} 40%, transparent)`, pointerEvents:"none" }}/>

              <div style={{ display:"flex", gap:8, overflowX:"auto", padding:"6px 24px 10px", scrollbarWidth:"none" }}>
                {CATS.map(c => (
                  <button key={c} onClick={()=>setCategory(c)} style={{
                    flexShrink: 0,
                    padding: "7px 15px",
                    fontSize: 11,
                    fontFamily: F.mono,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    borderRadius: 980,
                    transition: "all 0.2s",
                    border: `1px solid ${category===c ? C.text : C.border}`,
                    background: category===c ? C.text : C.card,
                    color: category===c ? C.bg : C.muted,
                    boxShadow: "none",
                  }}>
                    {c}
                  </button>
                ))}

              </div>
            </div>

            {/* ── REGION FILTER HEADER ── */}
            {regionFilter && (
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                background:C.surface, borderRadius:14,
                padding:"12px 16px", marginBottom:14,
                border:`1px solid ${C.border}`,
              }}>
                <div style={{display:"flex", alignItems:"center", gap:10}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <div>
                    <p style={{fontFamily:F.display, fontSize:14, fontWeight:700, color:C.text, margin:0}}>{regionFilter}</p>
                    <p style={{fontFamily:F.text, fontSize:11, color:C.muted, margin:0}}>Showing stories mentioning this location</p>
                  </div>
                </div>
                <button onClick={()=>setRegionFilter(null)} style={{
                  display:"flex", alignItems:"center", gap:5,
                  background:"none", border:`1px solid ${C.border}`,
                  borderRadius:99, padding:"5px 12px",
                  fontFamily:F.text, fontSize:12, color:C.muted,
                  cursor:"pointer",
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  Clear
                </button>
              </div>
            )}

            {/* Search results view */}
            {searchResults !== null && (
              <div style={{marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <p style={{fontFamily:F.text,fontSize:13,fontWeight:600,color:C.text,margin:0}}>{searchResults.length} results</p>
                  <button onClick={()=>setSearchResults(null)} style={{fontSize:11,color:C.muted,background:C.surface,border:`1px solid ${C.border}`,borderRadius:99,padding:"3px 10px",cursor:"pointer",fontFamily:F.text}}>Clear ✕</button>
                </div>
                {searchLoading && <div style={{display:"flex",gap:10,alignItems:"center",padding:"20px 0"}}><Spinner/><p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:0}}>Searching…</p></div>}
                {searchResults.map(a=>(
                  <div key={a.id} style={{marginBottom:10}}>
                    <ArticleCard a={a} onRead={onRead} bookmarks={bookmarks} setBookmarks={setBookmarks} setVerifying={setVerifying} onJournalist={setJournalist} isLead={false} isGrid={false} onCompare={setCompareStory} onBookmarkSync={syncBookmark}/>
                  </div>
                ))}
              </div>
            )}

            {searchResults === null && feed.length===0 && (
              <SkeletonFeed/>
            )}

            {searchResults === null && feed.length > 0 && (() => {
              // ── Score + pick the lead event for The Spectrum ──
              const scored = feed.map((a,i)=>({...a,_score:(a.breaking?4:0)+(a.image?1:0)+Math.max(0,12-i*0.25)}))
                                 .sort((a,b)=>b._score-a._score);
              const event = scored[0];
              const evCat = event.category;

              // Find the strongest article for each lean, preferring same category as the event
              const pickLens = (lean) => {
                const sameCat = scored.find(a=>a.lean===lean && a.category===evCat);
                return sameCat || scored.find(a=>a.lean===lean) || null;
              };
              const lensLeft   = pickLens("left");
              const lensCenter = pickLens("center");
              const lensRight  = pickLens("right");
              const lenses = [
                { lean:"left",   label:"Left",   a:lensLeft   },
                { lean:"center", label:"Center", a:lensCenter },
                { lean:"right",  label:"Right",  a:lensRight  },
              ];
              const usedIds = new Set([event.id, lensLeft?.id, lensCenter?.id, lensRight?.id].filter(Boolean));

              // Coverage spread for a given category (counts of left/center/right in the feed)
              const spreadFor = (cat) => {
                const inCat = feed.filter(a=>a.category===cat);
                return {
                  left:   inCat.filter(a=>a.lean==="left").length,
                  center: inCat.filter(a=>a.lean==="center").length,
                  right:  inCat.filter(a=>a.lean==="right").length,
                  total:  inCat.length,
                };
              };

              // The Wire — everything not in the spectrum, newest first
              const wire = scored.filter(a=>!usedIds.has(a.id));

              const ActionRow = ({a}) => (
                <div style={{display:"flex",gap:6,flexWrap:"wrap",paddingTop:10}} onClick={e=>e.stopPropagation()}>
                  {a.url&&<button onClick={()=>window.open(a.url,"_blank","noopener,noreferrer")} style={{...glassBtn(false),padding:"6px 12px",fontSize:11,fontWeight:600,fontFamily:F.mono,letterSpacing:"0.04em"}}>READ ↗</button>}
                  <button onClick={()=>{const add=!bookmarks.includes(a.id);setBookmarks(v=>add?[...v,a.id]:v.filter(x=>x!==a.id));if(onBookmarkSync)onBookmarkSync(a,add);}} style={{...glassBtn(bookmarks.includes(a.id)),padding:"6px 11px",fontSize:11,fontFamily:F.mono,letterSpacing:"0.04em"}}>{bookmarks.includes(a.id)?"SAVED":"SAVE"}</button>
                  <button onClick={()=>setVerifying(a)} style={{...glassBtn(false),padding:"6px 11px",fontSize:11,fontFamily:F.mono,letterSpacing:"0.04em"}}>FACT CHECK</button>
                  <button onClick={()=>{setDnaQuery(a);setTab("dna");}} style={{...glassBtn(false),padding:"6px 11px",fontSize:11,fontFamily:F.mono,letterSpacing:"0.04em"}}>DNA</button>
                </div>
              );

              // Source-spread bar (segment widths proportional to counts)
              const SpreadBar = ({cat}) => {
                const s = spreadFor(cat);
                const max = Math.max(s.left,s.center,s.right,1);
                const w = (n)=> 5 + Math.round((n/max)*16);
                return (
                  <span style={{display:"flex",gap:2,alignItems:"center"}}>
                    <span style={{fontFamily:F.mono,fontSize:8,color:C.muted,marginRight:3,letterSpacing:"0.04em"}}>{s.total} SRC</span>
                    <span style={{width:w(s.left),height:5,borderRadius:1,background:C.left,opacity:s.left?1:0.25}}/>
                    <span style={{width:w(s.center),height:5,borderRadius:1,background:C.center,opacity:s.center?1:0.25}}/>
                    <span style={{width:w(s.right),height:5,borderRadius:1,background:C.right,opacity:s.right?1:0.25}}/>
                  </span>
                );
              };

              return (
                <>
                  {/* ════ THE SPECTRUM ════ */}
                  <div style={{padding:"14px 2px 18px",borderBottom:`8px solid ${C.text}`,marginBottom:18}}>
                    <div style={{fontFamily:F.mono,fontSize:10,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",color:C.text,display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                      The Spectrum <span style={{color:C.muted}}>— one story, three sides</span>
                    </div>
                    <p style={{fontFamily:F.display,fontSize:21,fontWeight:800,lineHeight:1.15,letterSpacing:"-0.02em",color:C.text,margin:"0 0 5px"}}>{decodeHTML(event.headline)}</p>
                    <p style={{fontFamily:F.mono,fontSize:9.5,color:C.muted,letterSpacing:"0.04em",margin:"0 0 14px"}}>
                      {spreadFor(evCat).total} SOURCES · {event.breaking?"BREAKING · ":""}{evCat.toUpperCase()}
                    </p>

                    {lenses.map(({lean,label,a})=>(
                      <div key={lean}
                        onClick={()=>{ if(a){onRead(a.id);if(navigator.vibrate)navigator.vibrate(6);setExpandedCard(v=>v===("lens"+lean)?null:("lens"+lean));} }}
                        style={{display:"flex",gap:11,padding:"11px 0",borderTop:`1px solid ${C.divider}`,cursor:a?"pointer":"default"}}>
                        <div style={{width:3,alignSelf:"stretch",borderRadius:2,background:leanColor(lean),flexShrink:0}}/>
                        <div style={{flex:1,minWidth:0}}>
                          {a ? (
                            <>
                              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4,flexWrap:"wrap"}}>
                                <span style={{fontFamily:F.mono,fontSize:8.5,fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",color:leanColor(lean)}}>{label}</span>
                                <span style={{fontFamily:F.mono,fontSize:9,color:C.muted,letterSpacing:"0.03em"}}>{a.source}</span>
                              </div>
                              <p style={{fontFamily:F.display,fontSize:14,fontWeight:600,lineHeight:1.3,color:C.text,margin:0}}>{decodeHTML(a.headline)}</p>
                              {expandedCard===("lens"+lean) && <ActionRow a={a}/>}
                            </>
                          ) : (
                            <p style={{fontFamily:F.mono,fontSize:11,color:C.muted,letterSpacing:"0.03em",padding:"2px 0"}}>No {label.toLowerCase()} coverage yet</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ════ THE WIRE ════ */}
                  <div style={{fontFamily:F.mono,fontSize:10,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",color:C.text,display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    The Wire <span style={{color:C.muted}}>— newest first</span>
                  </div>
                  <div>
                    {wire.map((a,i)=>{
                      const isOpen = expandedCard===a.id;
                      const t = a.publishedAt ? new Date(a.publishedAt) : null;
                      const timeLabel = t ? t.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}).replace(" ","").toLowerCase() : "live";
                      return (
                        <div key={a.id}
                          onClick={()=>{onRead(a.id);if(navigator.vibrate)navigator.vibrate(6);setExpandedCard(v=>v===a.id?null:a.id);}}
                          style={{display:"flex",gap:12,padding:"13px 0",borderTop:i===0?"none":`1px solid ${C.divider}`,cursor:"pointer",alignItems:"flex-start"}}>
                          <div style={{fontFamily:F.mono,fontSize:9,color:C.muted,letterSpacing:"0.02em",width:42,flexShrink:0,paddingTop:2}}>{timeLabel}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontFamily:F.display,fontSize:14.5,fontWeight:600,lineHeight:1.28,letterSpacing:"-0.01em",color:C.text,margin:"0 0 6px"}}>{decodeHTML(a.headline)}</p>
                            <div style={{display:"flex",alignItems:"center",gap:9,flexWrap:"wrap"}}>
                              <span style={{fontFamily:F.mono,fontSize:9,color:C.muted,letterSpacing:"0.03em"}}>{a.source} · <span style={{color:leanColor(a.lean),fontWeight:600}}>{(a.lean||"center").toUpperCase()}</span></span>
                              <SpreadBar cat={a.category}/>
                            </div>
                            {isOpen && <ActionRow a={a}/>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <LoudClearFooter/>
                </>
              );
            })()}
          </>
        )}

        {tab==="map" && (
          <div style={{paddingTop:20}}>
            <HeatMap articles={all} onRegion={r=>{setRegionFilter(r===regionFilter?null:r);setTab("feed");}}/>
          </div>
        )}

        {tab==="balance" && (
          <div style={{paddingTop:20}}>
            <h2 style={{fontFamily:F.display,fontSize:22,fontWeight:700,color:C.text,margin:"0 0 4px",letterSpacing:"-0.02em"}}>Personal Echo Chamber</h2>
            <p style={{fontFamily:F.text,fontSize:13,color:C.muted,margin:"0 0 24px",lineHeight:1.5}}>Based on the stories you have opened. Read across the spectrum to keep the needle centered.</p>

            <BiasGauge history={history} allArticles={all}/>

            {history.length > 0 && (()=>{
              // Build a list of {readArticle, counterpart} pairs:
              // For each article the user read, find an unread article
              // in the SAME category but from the opposite lean
              const readArticles = history
                .map(id => all.find(a => a.id === id))
                .filter(Boolean);

              const oppositeLean = l => l === "left" ? "right" : l === "right" ? "left" : null;

              const suggestions = [];
              const usedIds = new Set(history);

              for (const read of readArticles) {
                if (!read.category || !read.lean || read.lean === "center") continue;
                const opp = oppositeLean(read.lean);
                const match = all.find(a =>
                  !usedIds.has(a.id) &&
                  a.category === read.category &&
                  a.lean === opp
                );
                if (match) {
                  usedIds.add(match.id); // don't reuse same suggestion
                  suggestions.push({ read, match });
                  if (suggestions.length >= 4) break;
                }
              }

              // Fallback: if no matched-topic suggestions, show any unread opposite-lean
              if (!suggestions.length) {
                const leftCount  = readArticles.filter(a => a.lean === "left").length;
                const rightCount = readArticles.filter(a => a.lean === "right").length;
                const wantedLean = leftCount > rightCount ? "right" : leftCount < rightCount ? "left" : null;
                if (!wantedLean) return null;
                const fallbacks = all
                  .filter(a => !history.includes(a.id) && a.lean === wantedLean)
                  .slice(0, 4);
                if (!fallbacks.length) return null;
                return (
                  <div>
                    <p style={{fontFamily:F.display,fontSize:15,fontWeight:700,color:C.text,margin:"0 0 4px"}}>Other perspectives</p>
                    <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:"0 0 14px"}}>
                      {"Stories from " + wantedLean + "-leaning sources you have not read yet."}
                    </p>
                    {fallbacks.map(a=>(
                      <div key={a.id} onClick={()=>{ onRead(a.id); setTab("feed"); }}
                        style={{display:"flex",gap:12,padding:"13px 0",borderBottom:"1px solid " + C.divider,cursor:"pointer",alignItems:"flex-start"}}>
                        <div style={{width:3,alignSelf:"stretch",borderRadius:2,background:leanColor(a.lean),flexShrink:0,minHeight:36}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontFamily:F.text,fontSize:13,fontWeight:500,color:C.text,margin:"0 0 4px",lineHeight:1.4}}>{decodeHTML(a.headline)}</p>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <span style={{fontFamily:F.text,fontSize:11,color:C.muted}}>{a.source}</span>
                            <span style={{fontSize:10,color:leanColor(a.lean),fontWeight:600,background:leanColor(a.lean)+"18",borderRadius:10,padding:"1px 6px",fontFamily:F.text}}>{a.lean}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }

              return (
                <div>
                  <p style={{fontFamily:F.display,fontSize:15,fontWeight:700,color:C.text,margin:"0 0 4px"}}>The other side</p>
                  <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:"0 0 14px"}}>
                    Same topic, opposite perspective — stories you have not read yet.
                  </p>
                  {suggestions.map(({read, match})=>(
                    <div key={match.id} onClick={()=>{ onRead(match.id); setTab("feed"); }}
                      style={{background:C.surface,borderRadius:14,marginBottom:10,overflow:"hidden",cursor:"pointer"}}>
                      {/* What you read */}
                      <div style={{padding:"10px 14px 8px",borderBottom:"1px solid " + C.divider, opacity:0.6}}>
                        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:3}}>
                          <span style={{fontFamily:F.text,fontSize:9,fontWeight:700,letterSpacing:"0.07em",color:C.muted,textTransform:"uppercase"}}>You read</span>
                          <span style={{fontSize:9,color:leanColor(read.lean),fontWeight:600,background:leanColor(read.lean)+"18",borderRadius:8,padding:"1px 5px",fontFamily:F.text}}>{read.lean}</span>
                        </div>
                        <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0,lineHeight:1.35}}>{decodeHTML(read.headline)}</p>
                      </div>
                      {/* The other side */}
                      <div style={{padding:"10px 14px 12px",borderLeft:"3px solid " + leanColor(match.lean)}}>
                        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                          <span style={{fontFamily:F.text,fontSize:9,fontWeight:700,letterSpacing:"0.07em",color:C.muted,textTransform:"uppercase"}}>Other side</span>
                          <span style={{fontSize:9,color:leanColor(match.lean),fontWeight:700,background:leanColor(match.lean)+"18",borderRadius:8,padding:"1px 5px",fontFamily:F.text}}>{match.lean}</span>
                          <span style={{fontFamily:F.text,fontSize:9,color:C.muted}}>{match.source}</span>
                        </div>
                        <p style={{fontFamily:F.text,fontSize:13,fontWeight:600,color:C.text,margin:0,lineHeight:1.4}}>{decodeHTML(match.headline)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}


        {tab==="dna" && <div style={{paddingTop:20}}><DNATree articles={all} initialArticle={dnaQuery} onClearQuery={()=>setDnaQuery(null)}/></div>}

        {tab==="profile" && (
          <div style={{paddingTop:20}}>

            {/* ── ACCOUNT SECTION ── */}
            {user ? (
              <div style={{background:C.surface,borderRadius:16,padding:"18px",marginBottom:24,display:"flex",alignItems:"center",gap:14}}>
                <div style={{
                  width:48,height:48,borderRadius:"50%",
                  background:C.orange,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:20,fontWeight:700,color:"#fff",fontFamily:F.display,flexShrink:0,
                }}>
                  {(user.user_metadata?.display_name||user.email||"?")[0].toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontFamily:F.display,fontSize:15,fontWeight:700,color:C.text,margin:"0 0 2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {user.user_metadata?.display_name || user.email?.split("@")[0]}
                  </p>
                  <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</p>
                </div>
                <button onClick={signOut} style={{
                  flexShrink:0,padding:"7px 14px",borderRadius:10,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.muted,fontFamily:F.text,fontSize:12,cursor:"pointer",
                }}>Sign Out</button>
              </div>
            ) : (
              <div style={{background:C.accentSoft,borderRadius:16,padding:"20px",marginBottom:24,border:`1px solid ${C.orange}30`}}>
                <p style={{fontFamily:F.display,fontSize:16,fontWeight:700,color:C.text,margin:"0 0 6px"}}>Save your preferences</p>
                <p style={{fontFamily:F.text,fontSize:13,color:C.muted,margin:"0 0 14px",lineHeight:1.5}}>
                  Sign in to sync bookmarks, reading history, and settings across all your devices.
                </p>
                <button onClick={()=>setShowAuth(true)} style={{
                  background:C.orange,border:"none",borderRadius:10,
                  padding:"10px 20px",fontSize:13,fontWeight:700,
                  color:"#fff",cursor:"pointer",fontFamily:F.text,
                  boxShadow:"0 2px 8px rgba(232,149,109,0.35)",
                }}>Sign In / Create Account</button>
              </div>
            )}

            {/* ── SETTINGS ── */}
            <h2 style={{fontFamily:F.display,fontSize:18,fontWeight:700,color:C.text,margin:"0 0 12px",letterSpacing:"-0.01em"}}>Settings</h2>
            <div style={{background:C.surface,borderRadius:16,overflow:"hidden",marginBottom:28}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px",borderBottom:`1px solid ${C.divider}`}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:18}}>{darkMode ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{display:"inline-block",verticalAlign:"middle"}}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{display:"inline-block",verticalAlign:"middle"}}><path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z"/></svg>)}</span>
                  <div>
                    <p style={{fontFamily:F.text,fontSize:14,fontWeight:600,color:C.text,margin:0}}>{darkMode?"Light Mode":"Dark Mode"}</p>
                    <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0}}>{darkMode?"Switch to light theme":"Switch to dark theme"}</p>
                  </div>
                </div>
                <div onClick={()=>setDarkMode(v=>!v)} style={{
                  width:48,height:28,borderRadius:14,
                  background:darkMode?C.orange:C.divider,
                  position:"relative",cursor:"pointer",
                  transition:"background 0.25s",flexShrink:0,
                }}>
                  <div style={{
                    position:"absolute",top:3,left:darkMode?22:3,
                    width:22,height:22,borderRadius:"50%",background:"#fff",
                    boxShadow:"0 1px 4px rgba(0,0,0,0.2)",
                    transition:"left 0.22s cubic-bezier(0.4,0,0.2,1)",
                  }}/>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px",borderBottom:`1px solid ${C.divider}`}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{flexShrink:0,color:C.muted}}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
                  <div>
                    <p style={{fontFamily:F.text,fontSize:14,fontWeight:600,color:C.text,margin:0}}>Feed Updates</p>
                    <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0}}>Refreshes every 15 minutes</p>
                  </div>
                </div>
                <span style={{fontFamily:F.text,fontSize:12,color:C.muted}}>Auto</span>
              </div>

              <div style={{padding:"16px 18px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom: (notifEnabled && notifPermission==="granted") ? 14 : 0}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{flexShrink:0,color:C.muted}}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>
                    <div>
                      <p style={{fontFamily:F.text,fontSize:14,fontWeight:600,color:C.text,margin:0}}>Morning Briefing</p>
                      <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0}}>
                        {notifPermission==="denied" ? "Blocked in browser settings"
                          : (notifEnabled && notifPermission==="granted") ? ("Daily briefing at " + notifTime)
                          : "Get a daily headline digest"}
                      </p>
                    </div>
                  </div>
                  {notifPermission === "denied" ? (
                    <span style={{fontFamily:F.text,fontSize:11,color:C.muted}}>Blocked</span>
                  ) : (
                    <div onClick={()=>{ if(!notifEnabled || notifPermission!=="granted"){ requestNotifPermission(); } else { disableNotifications(); } }} style={{
                      width:48, height:28, borderRadius:14,
                      background:(notifEnabled && notifPermission==="granted") ? C.orange : C.divider,
                      position:"relative", cursor:"pointer",
                      transition:"background 0.25s", flexShrink:0,
                    }}>
                      <div style={{
                        position:"absolute", top:3,
                        left:(notifEnabled && notifPermission==="granted") ? 22 : 3,
                        width:22, height:22, borderRadius:"50%", background:"#fff",
                        boxShadow:"0 1px 4px rgba(0,0,0,0.2)",
                        transition:"left 0.22s cubic-bezier(0.4,0,0.2,1)",
                      }}/>
                    </div>
                  )}
                </div>
                {(notifEnabled && notifPermission==="granted") && (
                  <div style={{display:"flex",alignItems:"center",gap:10,paddingTop:4}}>
                    <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0,flexShrink:0}}>Send at</p>
                    <input
                      type="time"
                      value={notifTime}
                      onChange={e=>{ setNotifTime(e.target.value); scheduleLocalBriefing(e.target.value); }}
                      style={{
                        fontFamily:F.text, fontSize:13, fontWeight:600,
                        color:C.text, background:C.card,
                        border:"1px solid " + C.border, borderRadius:8,
                        padding:"5px 10px", cursor:"pointer", outline:"none",
                      }}
                    />
                    <p style={{fontFamily:F.text,fontSize:11,color:C.muted,margin:0}}>local time</p>
                  </div>
                )}
              </div>
            </div>

            <div style={{height:1,background:C.divider,margin:"0 0 24px"}}/>

            {/* ── DAILY BRIEFING ── */}
            <h2 style={{fontFamily:F.display,fontSize:22,fontWeight:700,color:C.text,margin:"0 0 4px",letterSpacing:"-0.02em"}}>Daily Briefing</h2>
            <p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:"0 0 20px"}}>
              {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
            </p>
            {!briefing&&!briefingLoading&&(
              <button onClick={loadBriefing} style={{
                background:"#E8956D", border:"none", borderRadius:12,
                padding:"12px 24px", fontSize:14, fontWeight:600,
                color:"#fff", cursor:"pointer", fontFamily:F.text,
                boxShadow:"0 2px 10px rgba(232,149,109,0.35)", marginBottom:24,
              }}>Generate Briefing</button>
            )}
            {briefingLoading&&<div style={{display:"flex",gap:12,alignItems:"center",padding:"20px 0",marginBottom:16}}><Spinner/><p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:0}}>Crafting your briefing…</p></div>}
            {briefing&&(
              <div style={{marginBottom:32}}>
                <p style={{fontFamily:F.text,fontSize:15,color:C.sub,lineHeight:1.7,margin:"0 0 24px",paddingLeft:14,borderLeft:`3px solid ${C.accent}`}}>{briefing.overview}</p>
                {(briefing.stories||[]).map((s,i)=>(
                  <div key={i} style={{...glass(0.6),borderRadius:14,padding:"14px 16px",marginBottom:10}}>
                    <p style={{fontFamily:F.display,fontSize:15,fontWeight:600,color:C.text,margin:"0 0 6px"}}>{s.headline}</p>
                    <p style={{fontFamily:F.text,fontSize:13,color:C.muted,margin:0,lineHeight:1.6}}>{s.insight}</p>
                  </div>
                ))}
                <p style={{fontFamily:F.text,fontSize:14,color:C.sub,margin:"16px 0 0",lineHeight:1.6,fontStyle:"italic"}}>{briefing.uplifting}</p>
                <button onClick={loadBriefing} style={{...glassBtn(false),marginTop:14,padding:"9px 18px",fontSize:12}}>Regenerate</button>
              </div>
            )}

            {/* ── DIVIDER ── */}
            <div style={{height:1,background:C.divider,margin:"8px 0 24px"}}/>

            {/* ── SAVED STORIES ── */}
            <h2 style={{fontFamily:F.display,fontSize:22,fontWeight:700,color:C.text,margin:"0 0 4px",letterSpacing:"-0.02em"}}>Saved</h2>
            <p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:"0 0 20px"}}>{bookmarks.length} {bookmarks.length===1?"story":"stories"}</p>
            {bookmarks.length===0
              ? <p style={{fontFamily:F.text,fontSize:14,color:C.muted}}>Tap "Save" on any story to find it here.</p>
              : all.filter(a=>bookmarks.includes(a.id)).map((a,i)=>(
                  <ArticleCard key={a.id} a={a} onRead={onRead} bookmarks={bookmarks} setBookmarks={setBookmarks} setVerifying={setVerifying} onJournalist={setJournalist} isLead={i===0} isGrid={false} onCompare={setCompareStory} onBookmarkSync={syncBookmark}/>
                ))
            }

            {/* ── DIVIDER ── */}
            <div style={{height:1,background:C.divider,margin:"24px 0"}}/>

            {/* ── JOURNALIST PROFILES ── */}
            <h2 style={{fontFamily:F.display,fontSize:22,fontWeight:700,color:C.text,margin:"0 0 4px",letterSpacing:"-0.02em"}}>Sources</h2>
            <p style={{fontFamily:F.text,fontSize:14,color:C.muted,margin:"0 0 20px"}}>Tap any source to see their trust profile.</p>
            {Object.values(JOURNALISTS).map(j => {
              const trustColor = j.trustScore>=85?"#3A9E6A":j.trustScore>=65?"#5CB87A":"#80C994";
              return (
                <div key={j.outlet} onClick={()=>setJournalist(j.outlet)}
                  style={{...glass(0.65),borderRadius:16,padding:"14px 16px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:14}}>
                  <TrustMeter score={j.trustScore} size={48}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                      <span style={{fontFamily:F.display,fontSize:15,fontWeight:700,color:C.text}}>{j.outlet}</span>
                      {j.verified&&<span style={{fontSize:11,color:"#3A9E6A",fontWeight:600}}>✓</span>}
                    </div>
                    <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0}}>{j.beat}</p>
                  </div>
                  <div style={{textAlign:"center",flexShrink:0}}>
                    <div style={{fontFamily:F.display,fontSize:18,fontWeight:700,color:trustColor}}>{j.trustScore}</div>
                    <div style={{fontFamily:F.text,fontSize:9,color:C.muted}}>Trust</div>
                  </div>
                </div>
              );
            })}

            {/* ── DIVIDER ── */}
            <div style={{height:1,background:C.divider,margin:"24px 0"}}/>

            {/* ── ABOUT CLARION ── */}
            <div style={{marginBottom:40}}>
              <div style={{textAlign:"center",marginBottom:24}}>
                <ClarionLogoFull height={40}/>
                <div style={{fontFamily:F.mono,fontSize:9,fontWeight:600,color:C.accent,letterSpacing:"0.24em",textTransform:"uppercase",marginTop:7}}>Loud &amp; Clear</div>
              </div>

              {/* Mission */}
              <div style={{background:C.surface,borderRadius:16,padding:"18px 18px",marginBottom:12}}>
                <p style={{fontFamily:F.text,fontSize:10,fontWeight:700,color:C.orange,
                  letterSpacing:"0.1em",textTransform:"uppercase",margin:"0 0 8px"}}>Our Mission</p>
                <p style={{fontFamily:F.display,fontSize:16,fontWeight:700,color:C.text,
                  margin:"0 0 8px",lineHeight:1.35,letterSpacing:"-0.01em"}}>
                  An informed democracy needs citizens who read across the spectrum.
                </p>
                <p style={{fontFamily:F.text,fontSize:13,color:C.sub,lineHeight:1.7,margin:0}}>
                  Clarion aggregates news from left, center, and right-leaning sources and makes the bias visible — not to tell you what to think, but to make sure you see the full picture.
                </p>
              </div>

              {/* Features */}
              <div style={{background:C.surface,borderRadius:16,padding:"18px 18px",marginBottom:12}}>
                <p style={{fontFamily:F.text,fontSize:10,fontWeight:700,color:C.orange,
                  letterSpacing:"0.1em",textTransform:"uppercase",margin:"0 0 14px"}}>How It Works</p>
                {[
                  { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="1.8" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>, title:"Lean Ratings",    body:"Every story tagged left, center, or right based on the outlet's historical bias." },
                  { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="1.8" strokeLinecap="round"><path d="M7 3c0 4 10 4 10 8S7 15 7 19M17 3c0 4-10 4-10 8s10 4 10 8"/></svg>, title:"Story DNA",        body:"Trace how a story evolves across sources and flag spin or missing context." },
                  { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3L3 6v15l6-3 6 3 6-3V3l-6 3-6-3z"/><path d="M9 3v15M15 6v15"/></svg>, title:"World Map",        body:"Stories geo-tagged on a live map with connection lines between related locations." },
                  { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="1.8" strokeLinecap="round"><path d="M12 3v18M5 8l7-5 7 5M5 16l7 5 7-5"/></svg>, title:"Compare Coverage", body:"See how left, center, and right outlets frame the same headline differently." },
                  { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="1.8" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>, title:"Balance Meter",    body:"Track your reading diet across the political spectrum." },
                ].map(({icon,title,body}) => (
                  <div key={title} style={{display:"flex",gap:12,marginBottom:12,alignItems:"flex-start"}}>
                    <div style={{width:34,height:34,borderRadius:10,background:C.accentSoft,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{icon}</div>
                    <div style={{paddingTop:1}}>
                      <p style={{fontFamily:F.display,fontSize:13,fontWeight:700,color:C.text,margin:"0 0 2px"}}>{title}</p>
                      <p style={{fontFamily:F.text,fontSize:12,color:C.muted,margin:0,lineHeight:1.55}}>{body}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Lean score guide */}
              <div style={{background:C.surface,borderRadius:16,padding:"18px 18px",marginBottom:12}}>
                <p style={{fontFamily:F.text,fontSize:10,fontWeight:700,color:C.orange,
                  letterSpacing:"0.1em",textTransform:"uppercase",margin:"0 0 12px"}}>Lean Score Guide</p>
                {[
                  { color:C.left,   label:"◀ Left",   desc:"NYT, Guardian, NPR, CNN" },
                  { color:C.center, label:"● Center",  desc:"Reuters, AP, BBC, Bloomberg" },
                  { color:C.right,  label:"▶ Right",   desc:"Fox News, WSJ, Breitbart" },
                ].map(({color,label,desc}) => (
                  <div key={label} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <span style={{fontFamily:F.text,fontSize:11,fontWeight:700,color:"#fff",
                      background:color,borderRadius:20,padding:"3px 12px",flexShrink:0}}>{label}</span>
                    <span style={{fontFamily:F.text,fontSize:12,color:C.muted}}>{desc}</span>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <p style={{fontFamily:F.text,fontSize:11,color:C.muted,textAlign:"center",margin:"16px 0 0"}}>
                theclarion.app · Powered by Claude AI
              </p>
            </div>

          </div>
        )}

      </div>

      {/* ── INSTAGRAM-STYLE BOTTOM NAV ── */}
      <div style={{
        position:"fixed", bottom:0, left:0, right:0, zIndex:200,
        background: C.bg,
        borderTop: `1px solid ${C.border}`,
        borderRadius:"20px 20px 0 0",
        boxShadow: "none",
        padding:"10px 8px 20px",
      }}>
        <div style={{
          display:"flex", justifyContent:"space-around", alignItems:"center",
          maxWidth:480, margin:"0 auto",
        }}>
          {[
            { id:"feed",    label:"Feed",    svg:<path d="M3 5h18M3 10h18M3 15h12" strokeWidth="1.8" strokeLinecap="round"/> },
            { id:"map",     label:"Map",     svg:<><path d="M9 3L3 6v15l6-3 6 3 6-3V3l-6 3-6-3z" strokeWidth="1.6" strokeLinejoin="round"/><path d="M9 3v15M15 6v15" strokeWidth="1.6"/></> },
            { id:"balance", label:"Balance", svg:<><circle cx="12" cy="12" r="1" fill="currentColor"/><path d="M12 12 L6 7 M12 12 L18 7 M5 17h14M12 4v2" strokeWidth="1.7" strokeLinecap="round"/></> },
            { id:"dna",     label:"DNA",     svg:<><path d="M7 3c0 4 10 4 10 8S7 15 7 19M17 3c0 4-10 4-10 8s10 4 10 8" strokeWidth="1.7" strokeLinecap="round"/></> },
            { id:"profile", label:"Profile", svg:<><circle cx="12" cy="8" r="4" strokeWidth="1.7"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeWidth="1.7" strokeLinecap="round"/></> },
          ].map(n => {
            const active = tab === n.id;
            return (
              <button
                key={n.id}
                onClick={() => switchTab(n.id)}
                style={{
                  display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                  background:"none", border:"none", cursor:"pointer",
                  padding:"4px 6px", borderRadius:14,
                  transition:"all 0.18s",
                  minWidth:44,
                }}
              >
                <div style={{
                  width:44, height:32,
                  borderRadius:11,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  transition:"all 0.18s",
                  background: active ? C.accentSoft : "transparent",
                  transform: active ? "scale(1.08)" : "scale(1)",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke={active ? C.orange : C.muted} strokeWidth="1.7"
                    style={{transition:"all 0.18s"}}>
                    {n.svg}
                  </svg>
                </div>
                <span style={{
                  fontFamily:F.mono, fontSize:8, fontWeight:700,
                  color: active ? C.orange : C.muted,
                  letterSpacing:"0.08em", textTransform:"uppercase",
                  transition:"all 0.18s",
                }}>
                  {n.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return <ErrorBoundary><ClarionFinal /></ErrorBoundary>;
}
